/* eslint-disable no-useless-escape */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import { X, Mic, ThumbsUp, ThumbsDown, MessageSquare, Copy } from 'lucide-react';
import { chatAPI } from '../api/axios';
import CountrySelector from './CountrySelector';
import './ChatbotFixed.css';
import './ChatbotZoomFix.css';

// Memoized components for better performance
const MemoizedButton = memo(({ onClick, style, title, children, disabled }) => (
  <button onClick={onClick} style={style} title={title} disabled={disabled}>
    {children}
  </button>
));



const ChatbotFixed = () => {
  const [input, setInput] = useState('');
  const [isOpen, setIsOpen] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [feedbackStates, setFeedbackStates] = useState({});
  const [editingMessage, setEditingMessage] = useState(null);
  const [editText, setEditText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentSpeakingMessage, setCurrentSpeakingMessage] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    // Generate new session ID for each conversation (don't persist)
    const newId = 'session-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
    console.log('Created new session ID:', newId);
    return newId;
  });
  const [conversationHistory, setConversationHistory] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState({ code: 'IN', name: 'India', flag: 'https://flagcdn.com/w40/in.png', language: 'EN' });

  const [currentAudio, setCurrentAudio] = useState(null);
  const [isAudioPaused, setIsAudioPaused] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [progressInterval, setProgressInterval] = useState(null);
  const [magicPopup, setMagicPopup] = useState({ show: false, queries: [], messageIndex: null });
  const [magicLoading, setMagicLoading] = useState(null);
  const [magicCache, setMagicCache] = useState({});
  const [isEditLoading, setIsEditLoading] = useState(false);

  const [expandedFareDetails, setExpandedFareDetails] = useState({});
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const chatContainerRef = useRef(null);
  


  // Initialize Media Recorder for Whisper
  useEffect(() => {
    // Empty useEffect for future functionality
  }, []);

  // Handle microphone button click with Whisper API
  const handleMicrophoneClick = async () => {
    if (isListening) {
      // Stop recording
      if (window.mediaRecorder && window.mediaRecorder.state === 'recording') {
        window.mediaRecorder.stop();
      }
      setIsListening(false);
    } else {
      // Start recording
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            sampleRate: 16000,
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true
          } 
        });
        
        // Try MP3 first, then M4A, then WebM as fallback
        let options = {};
        if (MediaRecorder.isTypeSupported('audio/mp4')) {
          options = { mimeType: 'audio/mp4' };
        } else if (MediaRecorder.isTypeSupported('audio/mpeg')) {
          options = { mimeType: 'audio/mpeg' };
        } else {
          options = { mimeType: 'audio/webm;codecs=opus' };
        }
        
        console.log('Using MediaRecorder with options:', options);
        const mediaRecorder = new MediaRecorder(stream, options);
        const audioChunks = [];
        
        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunks.push(event.data);
          }
        };
        
        mediaRecorder.onstop = async () => {
          try {
            const mimeType = mediaRecorder.mimeType || 'audio/mp4';
            const audioBlob = new Blob(audioChunks, { type: mimeType });
            
            console.log('Audio blob size:', audioBlob.size, 'bytes');
            console.log('Audio blob type:', audioBlob.type);
            
            if (audioBlob.size === 0) {
              alert('No audio recorded. Please try again.');
              return;
            }
            
            if (audioBlob.size < 1000) {
              alert('Audio recording too short. Please speak for at least 1 second.');
              return;
            }
            
            let fileName = 'recording.mp4';
            let fileType = mimeType;
            
            if (mimeType.includes('mpeg')) {
              fileName = 'recording.mp3';
            } else if (mimeType.includes('webm')) {
              fileName = 'recording.webm';
            }
            
            const audioFile = new File([audioBlob], fileName, { 
              type: fileType,
              lastModified: Date.now()
            });
            
            console.log('Sending audio file:', audioFile.name, audioFile.type, audioFile.size, 'bytes');
            
            const response = await chatAPI.speechToText(audioFile);
            const transcribedText = response.data.text || response.data.transcript;
            
            if (transcribedText && transcribedText.trim()) {
              setInput(transcribedText);
            } else {
              alert('No speech detected. Please try speaking clearly and try again.');
            }
          } catch (error) {
            console.error('Transcription error:', error);
            console.error('Error response:', error.response?.data);
            console.error('Error status:', error.response?.status);
            console.error('Error detail:', error.response?.data?.detail);
            
            if (error.code === 'ERR_NETWORK') {
              alert('Network error. Please check if the backend server is running and try again.');
            } else if (error.response?.status === 422) {
              const detail = error.response?.data?.detail;
              alert(`Validation error: ${Array.isArray(detail) ? detail[0]?.msg || detail[0] : detail || 'Audio format not supported'}`);
            } else if (error.response?.status === 413) {
              alert('Audio file too large. Please record a shorter message.');
            } else if (error.response?.status === 500) {
              alert('Server error processing audio. Please try again.');
            } else {
              alert('Failed to transcribe audio. Please check your connection and try again.');
            }
          }
          
          // Stop all tracks
          stream.getTracks().forEach(track => track.stop());
        };
        
        window.mediaRecorder = mediaRecorder;
        mediaRecorder.start(1000); // Collect data every second
        setIsListening(true);
        
      } catch (error) {
        console.error('Microphone permission error:', error);
        alert('Microphone access is required for voice input. Please allow microphone permission and try again.');
      }
    }
  };

  // Language detection for TTS
  const detectLanguage = (text) => {
    // eslint-disable-next-line no-useless-escape
    const arabicRegex = /[\u0600-\u06FF]/;
    const japaneseRegex = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/;
    
    if (arabicRegex.test(text)) return 'ar';
    if (japaneseRegex.test(text)) return 'ja';
    return 'en';
  };

  // Function to apply RTL styling based on content
  const applyRTLStyling = (text) => {
    if (!text) return false;
    
    // Don't apply RTL to timestamps or pure numbers
    const timeRegex = /^\d{1,2}:\d{2}\s*(AM|PM)?$/i;
    const numberRegex = /^[\d\s:.,\-\/]+$/;
    
    if (timeRegex.test(text.trim()) || numberRegex.test(text.trim())) {
      return false;
    }
    
    const arabicRegex = /[\u0600-\u06FF\u0750-\u077F]/;
    return arabicRegex.test(text);
  };



  // Handle TTS button click
  const handleTTSClick = (text, messageIndex) => {
    if (isSpeaking) {
      // Stop current audio
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
        setCurrentAudio(null);
      }
      // Also stop browser TTS as fallback
      speechSynthesis.cancel();
      setIsSpeaking(false);
      setCurrentSpeakingMessage(null);
      setIsAudioPaused(false);
    } else {
      // Start new speech
      setCurrentSpeakingMessage(messageIndex);
      speakMessage(text);
    }
  };

  // Speak specific message using Google TTS with browser fallback
  const speakMessage = async (text) => {
    const language = detectLanguage(text);
    
    try {
      // Show immediate loading feedback
      setIsSpeaking(true);
      setIsAudioPaused(false);
      
      // Use Google TTS for Arabic or as fallback
      const executeGoogleTTS = async () => {
        try {
          let cleanText, response;
          
          if (language === 'ar') {
            cleanText = text
              .replace(/<[^>]*>/g, '')
              .replace(/\[IMAGE:[^\]]+\]/g, '')
              .replace(/[^\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\w\s.,!?؟،؛ـ]/g, '')
              .trim();
            response = await chatAPI.getArabicTTS(cleanText);
            console.log('Using Google TTS for Arabic');
          } else if (language === 'en') {
            cleanText = text
              .replace(/<[^>]*>/g, '')
              .replace(/\[IMAGE:[^\]]+\]/g, '')
              .replace(/[^\w\s.,!?;:"'-]/g, '')
              .trim();
            response = await chatAPI.getEnglishTTS(cleanText);
            console.log('Using Google TTS for English fallback');
          } else {
            setIsSpeaking(false);
            return;
          }
          
          const audioBase64 = response.data.audio;
          const audio = new Audio(`data:audio/mp3;base64,${audioBase64}`);
          setCurrentAudio(audio);
      
          audio.onloadedmetadata = () => {
            setAudioDuration(audio.duration);
          };
          
          audio.ontimeupdate = () => {
            setAudioProgress((audio.currentTime / audio.duration) * 100);
          };
          
          audio.onended = () => {
            setIsSpeaking(false);
            setCurrentSpeakingMessage(null);
            setCurrentAudio(null);
            setIsAudioPaused(false);
            setAudioProgress(0);
            setAudioDuration(0);
          };
          
          audio.onerror = () => {
            console.error('Audio playback error');
            setIsSpeaking(false);
            setCurrentAudio(null);
            setIsAudioPaused(false);
            setAudioProgress(0);
            setAudioDuration(0);
          };
          
          audio.onpause = () => {
            setIsAudioPaused(true);
          };
          
          audio.onplay = () => {
            setIsAudioPaused(false);
          };
          
          // Preload and play immediately
          audio.preload = 'auto';
          const playPromise = audio.play();
          
          // Handle play promise for better browser compatibility
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              console.error('Audio play failed:', error);
              setIsSpeaking(false);
              setIsAudioPaused(false);
            });
          }
        } catch (error) {
          console.error('Google TTS error:', error);
          setIsSpeaking(false);
          setIsAudioPaused(false);
        }
      };
      
      // Use browser TTS only for English (Arabic needs Google TTS for quality)
      if (language === 'en' && 'speechSynthesis' in window) {
        const cleanTextForTTS = text.replace(/\[IMAGE:[^\]]+\]/g, '').trim();
        const utterance = new SpeechSynthesisUtterance(cleanTextForTTS);
        utterance.lang = 'en-US';
        utterance.rate = 0.9;
        utterance.pitch = 1;
        
        utterance.onstart = () => {
          console.log('Browser TTS started for English');
          
          // Estimate duration for browser TTS (rough calculation)
          const estimatedDuration = Math.max(15, text.length * 0.08); // ~0.08 seconds per character
          setAudioDuration(estimatedDuration);
          setAudioProgress(0);
          
          // Start progress simulation for browser TTS
          let currentTime = 0;
          const interval = setInterval(() => {
            currentTime += 0.1;
            if (currentTime >= estimatedDuration) {
              clearInterval(interval);
              setProgressInterval(null);
              return;
            }
            setAudioProgress((currentTime / estimatedDuration) * 100);
          }, 100);
          
          setProgressInterval(interval);
        };
        
        utterance.onend = () => {
          if (progressInterval) {
            clearInterval(progressInterval);
            setProgressInterval(null);
          }
          setIsSpeaking(false);
          setCurrentSpeakingMessage(null);
          setCurrentAudio(null);
          setIsAudioPaused(false);
          setAudioProgress(0);
          setAudioDuration(0);
        };
        
        utterance.onerror = () => {
          console.log('Browser TTS failed, falling back to Google TTS');
          // Only fallback if still speaking (not manually stopped)
          if (isSpeaking) {
            executeGoogleTTS();
          }
        };
        
        speechSynthesis.speak(utterance);
        return;
      }

      
      // Execute Google TTS
      await executeGoogleTTS();
      
    } catch (error) {
      console.error(`${language} TTS error:`, error);
      console.error('Error details:', error.response?.data || error.message);
      setIsSpeaking(false);
      setIsAudioPaused(false);
      
      // Show more specific error message
      if (error.response?.status === 404) {
        alert(`${language.toUpperCase()} TTS service not available. Make sure backend is running on port 8000.`);
      } else if (error.response?.status === 500) {
        alert(`Server error generating ${language} speech. Check if gTTS is installed: pip install gtts`);
      } else if (error.code === 'ERR_NETWORK') {
        alert('Network error. Check if backend server is running.');
      } else {
        const errorMsg = language === 'ar' ? 
          'خطأ في تشغيل الصوت العربي' : 
          'Error playing English audio';
        alert(errorMsg + ': ' + (error.message || 'Unknown error'));
      }
    }
  };

  // Optimized scroll to bottom with throttling
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);
  
  useEffect(() => {
    const timeoutId = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timeoutId);
  }, [chatMessages.length, scrollToBottom]);

  // Camera capture function
  const handleCameraCapture = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } // Use back camera for documents
      });
      
      // Create video element
      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();
      
      // Create modal for camera
      const modal = document.createElement('div');
      modal.style.cssText = `
        position: fixed; top: 0; left: 0; width: 100%; height: 100%;
        background: rgba(0,0,0,0.9); z-index: 10000; display: flex;
        flex-direction: column; align-items: center; justify-content: center;
      `;
      
      video.style.cssText = 'width: 90%; max-width: 400px; border-radius: 8px;';
      
      const captureBtn = document.createElement('button');
      captureBtn.textContent = '📸 Capture';
      captureBtn.style.cssText = `
        margin: 20px; padding: 12px 24px; background: #004A97;
        color: white; border: none; border-radius: 8px; font-size: 16px;
      `;
      
      const closeBtn = document.createElement('button');
      closeBtn.textContent = '✕ Close';
      closeBtn.style.cssText = `
        margin: 10px; padding: 8px 16px; background: #666;
        color: white; border: none; border-radius: 8px;
      `;
      
      modal.appendChild(video);
      modal.appendChild(captureBtn);
      modal.appendChild(closeBtn);
      document.body.appendChild(modal);
      
      captureBtn.onclick = () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0);
        
        canvas.toBlob((blob) => {
          const file = new File([blob], 'passport-scan.jpg', { type: 'image/jpeg' });
          setSelectedImage({ 
            file, 
            preview: URL.createObjectURL(file)
          });
          
          // Auto-send with passport analysis text in appropriate language
          const isArabicFlow = window.isArabicPassportFlow;
          setTimeout(() => {
            setInput(isArabicFlow ? 'استخراج معلومات المسافر من هذه الصورة' : 'Extract passenger info from this image');
            setTimeout(() => {
              handleSend();
            }, 100);
          }, 500);
          
          // Reset passport flag
          window.isArabicPassportFlow = false;
        });
        
        stream.getTracks().forEach(track => track.stop());
        document.body.removeChild(modal);
      };
      
      closeBtn.onclick = () => {
        stream.getTracks().forEach(track => track.stop());
        document.body.removeChild(modal);
      };
      
    } catch (error) {
      alert('Camera access denied. Please use the upload option instead.');
    }
  }, []);



  const handleImageSelect = useCallback((e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate image format
      const supportedFormats = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
      
      if (!supportedFormats.includes(fileExtension)) {
        // Show error in chatbot
        setChatMessages((prev) => [
          ...prev,
          {
            id: `msg-${Date.now()}`,
            sender: 'Assistant',
            text: `I'm sorry, but I cannot process ${fileExtension.toUpperCase()} files. I can only analyze images in these formats: ${supportedFormats.map(f => f.toUpperCase()).join(', ')}.\n\nPlease upload a supported image format and I'll be happy to help you analyze it.`,
            isPromptBubble: true,
            prompts: ['Try another image', 'Ask another question'],
            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          }
        ]);
        e.target.value = ''; // Clear the input
        return;
      }
      
      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        // Show error in chatbot
        setChatMessages((prev) => [
          ...prev,
          {
            id: `msg-${Date.now()}`,
            sender: 'Assistant',
            text: `I'm sorry, but the image you selected is ${(file.size / (1024 * 1024)).toFixed(1)}MB, which is larger than my 10MB limit. Could you please try uploading a smaller image? You can resize it or choose a different image.`,
            isPromptBubble: true,
            prompts: ['Try smaller image', 'Ask another question'],
            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          }
        ]);
        e.target.value = ''; // Clear the input
        return;
      }
      
      setSelectedImage({
        file,
        preview: URL.createObjectURL(file)
      });
      
      // Check if this was from passport upload context
      const isPassportUpload = document.activeElement && 
                              (document.activeElement.getAttribute('data-passport') === 'true' ||
                               window.lastActionWasPassport);
      
      if (isPassportUpload) {
        const isArabicFlow = window.isArabicPassportFlow;
        // Show the query in input field first, then auto-send
        const passportQuery = isArabicFlow ? 'استخراج معلومات المسافر من هذه الصورة' : 'Extract passenger info from this image';
        
        setTimeout(() => {
          setInput(passportQuery);
          setTimeout(() => {
            handleSend();
          }, 100);
        }, 500);
        
        // Reset passport flags
        window.lastActionWasPassport = false;
        window.isArabicPassportFlow = false;
      } else {
        // Show success message for regular image upload
        const isArabicFlow = window.isArabicImageFlow || selectedCountry?.language === 'AR';
        
        // Check if this is from passport upload (Arabic or English)
        const isFromPassportButton = window.isArabicPassportFlow || window.lastActionWasPassport;
        
        setChatMessages((prev) => [
          ...prev,
          {
            id: `msg-${Date.now()}`,
            sender: 'Assistant',
            text: isArabicFlow ? 
              'تم رفع الصورة بنجاح! يمكنك الآن إرسال رسالتك لتحليل الصورة.' : 
              'Image uploaded successfully! You can now send your message to analyze the image.',
            isPromptBubble: true,
            prompts: isArabicFlow ? 
              (isFromPassportButton ? 
                ['استخراج معلومات المسافر', 'حلل هذه الصورة', 'وصف الصورة'] :
                ['حلل هذه الصورة', 'ماذا ترى؟', 'وصف الصورة']) : 
              (isFromPassportButton ? 
                ['Extract passenger info', 'Analyze this image', 'Describe the image'] :
                ['Analyze this image', 'What do you see?', 'Describe the image']),
            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          }
        ]);
        
        // Reset Arabic image flow flag
        window.isArabicImageFlow = false;
      }
    }
  }, []);

  const handleRemoveImage = useCallback(() => {
    setSelectedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Generate magic questions in background
  // eslint-disable-next-line no-unused-vars
  const generateMagicQuestions = useCallback((messageText, magicKey) => {
    console.log('Generating magic questions for:', messageText, 'Key:', magicKey);
    
    const magicPrompt = `Generate exactly 3 short follow-up questions for: "${messageText}". Return only JSON array: ["Question 1", "Question 2", "Question 3"]`;
    
    chatAPI.sendMessage({
      user: { id: sessionId, name: 'Demo User' },
      query: magicPrompt,
      conversation_history: conversationHistory,
      country: selectedCountry?.code
    }).then(response => {
      const data = response.data;
      let queries = [];
      
      console.log('Magic generation response:', data.response);
      
      try {
        // Try to extract JSON array from response
        const jsonMatch = data.response.match(/\[.*?\]/s);
        if (jsonMatch) {
          queries = JSON.parse(jsonMatch[0]);
          console.log('Parsed queries:', queries);
        }
      } catch (parseError) {
        console.log('JSON parse failed, using fallback');
        // Fallback: split by lines and take first 3
        queries = data.response.split('\n').filter(q => q.trim() && !q.includes('JSON')).slice(0, 3);
      }
      
      // Ensure we have valid queries
      if (!queries || queries.length === 0) {
        console.log('No queries generated, using fallback');
        
        // Check if this is for a flight booking context

        
        // Don't use hardcoded fallback - let backend handle related queries
        queries = [];
      }
      
      // Pre-fetch responses for each query
      Promise.all(queries.slice(0, 3).map(query => 
        chatAPI.sendMessage({
          user: { id: sessionId, name: 'Demo User' },
          query: query,
          conversation_history: conversationHistory,
          country: selectedCountry?.code
        }).then(queryResponse => ({
          query: query,
          response: queryResponse.data.response,
          agent: queryResponse.data.agent,
          images: queryResponse.data.images,
          html_card: queryResponse.data.html_card,
          related_queries: queryResponse.data.related_queries
        })).catch(error => {
          console.error(`Error pre-fetching response for "${query}":`, error);
          return { query: query, response: `For more about "${query}", contact customer service or visit jazeera.com` };
        })
      )).then(preloadedResponses => {
        console.log('Magic cache updated for key:', magicKey, 'Queries:', queries);
        setMagicCache(prev => ({
          ...prev,
          [magicKey]: {
            queries: queries.slice(0, 3),
            responses: preloadedResponses
          }
        }));
        setChatMessages(prev => [...prev]); // Force re-render to show magic button
      });
    }).catch(err => {
      console.error('Background magic generation error:', err);
      console.error('Error details:', err.response?.data || err.message);
      
      // Check if this is for a flight booking context
      const isFlightContext = messageText.toLowerCase().includes('flight') || 
                            messageText.toLowerCase().includes('dubai') || 
                            messageText.toLowerCase().includes('kuwait') ||
                            messageText.toLowerCase().includes('booking') ||
                            // Check conversation history for flight context
                            conversationHistory.some(msg => 
                              msg.query?.toLowerCase().includes('flight') ||
                              msg.query?.toLowerCase().includes('dubai') ||
                              msg.query?.toLowerCase().includes('kuwait') ||
                              msg.response?.toLowerCase().includes('flight')
                            );
      
      console.log('Flight context detected:', isFlightContext);
      
      const fallbackQueries = isFlightContext 
        ? ['What are the flight times?', 'How much does it cost?', 'Can I book this flight?']
        : ['Ask another question', 'Get more info', 'Contact support'];
      
      console.log('Using fallback queries for magic assistant:', fallbackQueries);
      setMagicCache(prev => ({
        ...prev,
        [magicKey]: {
          queries: fallbackQueries,
          responses: []
        }
      }));
      setChatMessages(prev => [...prev]); // Force re-render to show magic button
    });
  }, [sessionId, conversationHistory, selectedCountry?.code]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSend = useCallback(async () => {
    if (!input.trim() && !selectedImage) return;
    if (isLoading) return;


    const userMessage = { 
      id: `user-${Date.now()}`,
      sender: 'user', 
      text: input,
      hasImage: !!selectedImage,
      imagePreview: selectedImage ? selectedImage.preview : null,
      timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
    
    setChatMessages((prev) => [
      ...prev,
      userMessage,
      { id: `typing-${Date.now()}`, sender: 'Assistant', text: '', isTyping: true },
    ]);
    
    const currentInput = input;
    const currentImage = selectedImage;
    setInput('');
    setSelectedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setIsLoading(true);
    
    // Magic questions will be populated from backend response
    
    try {
      let response;
      if (currentImage) {
        const formData = new FormData();
        formData.append('image', currentImage.file);
        formData.append('query', currentInput || 'Please analyze this image');
        formData.append('user', JSON.stringify({ id: sessionId, name: 'Demo User' }));
        formData.append('conversation_history', JSON.stringify(conversationHistory));
        formData.append('country', selectedCountry?.code || '');
        
        response = await chatAPI.sendMessageWithImage(formData);
      } else {
        let backendContext = null;
        try {
          const contextResponse = await chatAPI.getContext(sessionId);
          backendContext = contextResponse.data;
        } catch (err) {
          console.log('Could not fetch backend context:', err);
        }

        response = await chatAPI.sendMessage({
          user: { id: sessionId, name: 'Demo User' }, 
          query: currentInput,
          conversation_history: conversationHistory,
          country: selectedCountry?.code,
          context: {
            backendContext: backendContext
          }
        });
      }
      
      const data = response.data;
      
      // Check agent status from backend first, then localStorage
      try {
        let disabledAgents = [];
        
        try {
          const statusResponse = await chatAPI.getDashboardStatus();
          const statusData = statusResponse.data;
          disabledAgents = statusData.disabled_agents || [];
        } catch (err) {
          // Fallback to localStorage
          disabledAgents = JSON.parse(localStorage.getItem('chatbot_disabled_agents') || '[]');
        }
        
        const agentName = data.agent;
        
        if (agentName && disabledAgents.includes(agentName)) {
          const agentDisplayName = agentName.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' ');
          data.response = `⚠️ ${agentDisplayName} is currently under maintenance. This service is temporarily unavailable. Please try again later or contact support for assistance.`;
        }
      } catch (err) {
        console.log('Could not check agent status:', err);
      }
      
      if (data.session_id) {
        setSessionId(data.session_id);
      }
      
      if (data.conversation_history) {
        setConversationHistory(data.conversation_history);
      }
      
      // Add backend related_queries to magic cache and pre-fetch responses
      if (data.related_queries && data.related_queries.length > 0) {
        const magicKey = `magic_${userMessage.id}`;
        
        // Pre-fetch responses for each query
        Promise.all(data.related_queries.slice(0, 3).map(query => 
          chatAPI.sendMessage({
            user: { id: sessionId, name: 'Demo User' },
            query: query,
            conversation_history: conversationHistory,
            country: selectedCountry?.code
          }).then(queryResponse => ({
            query: query,
            response: queryResponse.data.response,
            agent: queryResponse.data.agent,
            images: queryResponse.data.images,
            html_card: queryResponse.data.html_card,
            related_queries: queryResponse.data.related_queries
          })).catch(error => {
            console.error(`Error pre-fetching response for "${query}":`, error);
            return { query: query, response: `For more about "${query}", contact customer service.` };
          })
        )).then(preloadedResponses => {
          setMagicCache(prev => ({
            ...prev,
            [magicKey]: {
              queries: data.related_queries,
              responses: preloadedResponses
            }
          }));
        });
      }
      
      setChatMessages((prev) => {
        const updated = [...prev];
        updated.pop();
        
        const isHtmlCard = data.html_card && data.html_card.trim().length > 0;
        
        updated.push({
          id: `msg-${Date.now()}`,
          sender: 'Assistant',
          text: data.response || 'Sorry, I couldn\'t process your request.',
          agent: data.agent || 'unknown',
          images: data.images,
          html_card: data.html_card,
          isHtmlCard: isHtmlCard,
          isPromptBubble: true,
          prompts: data.related_queries || [],
          related_queries: data.related_queries,
          timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
        });
        
        return updated;
      });
    } catch (err) {
      console.error('Chat error:', err);
      
      let errorMessage = `Sorry, there was an error processing your request: ${err.message}. Please try again.`;
      
      if (err.response?.data?.error) {
        const serverError = err.response.data.error;
        if (serverError.includes('Unsupported image format')) {
          errorMessage = `${serverError}\n\nSupported formats: JPG, JPEG, PNG, GIF, WEBP, BMP`;
        } else if (serverError.includes('Image file too large')) {
          errorMessage = serverError;
        } else {
          errorMessage = serverError;
        }
      }
      
      setChatMessages((prev) => {
        const updated = [...prev];
        updated.pop();
        return [
          ...updated,
          {
            id: `msg-${Date.now()}`,
            sender: 'Assistant',
            text: errorMessage,
            isPromptBubble: true,
            prompts: ['Try again', 'Upload different image', 'Ask another question']
          },
        ];
      });
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, selectedImage, isLoading, sessionId, conversationHistory, selectedCountry?.code]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isLoading && (input.trim() || selectedImage)) {
        handleSend();
      }
    }
  }, [isLoading, input, selectedImage]);



  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line no-unused-vars
  const toggleChat = useCallback(() => {
    setIsOpen((prevOpen) => {
      const justOpened = !prevOpen && chatMessages.length === 0;
      if (justOpened) {
        // Generate new session ID for new conversation
        const newId = 'session-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
        setSessionId(newId);
        console.log('New conversation started with session ID:', newId);
        
        setChatMessages([
          {
            id: `msg-${Date.now()}`,
            sender: 'Assistant',
            text: 'Here are some popular prompts for you to try',
            isPromptBubble: true,
            isInitialPrompt: true,
            prompts: ['Book a flight', 'check your flight Status', 'Ask general queries']
          },
        ]);
      }
      if (!prevOpen) {
        setIsMinimized(false);
      } else {
        // Auto-open after 2 seconds when closing via toggle
        setTimeout(() => {
          setIsOpen(true);
        }, 2000);
      }
      return !prevOpen;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMessages.length]);

  // Memoized initial prompts
  // eslint-disable-next-line no-unused-vars
  const initialPrompts = useMemo(() => ({
    EN: ['Book a flight', 'Flight status', 'General query', 'Baggage allowance'],
    AR: ['حجز رحلة', 'حالة الرحلة', 'استفسار عام', 'بدل الأمتعة']
  }), []);

  // Initialize with prompt box on page load
  useEffect(() => {
    if (chatMessages.length === 0) {
      const prompts = selectedCountry?.language === 'AR' 
        ? ['عرض رحلات إلى دبي', 'ما هو أفضل مكان للزيارة في ديسمبر', 'تحقق من حالة الرحلة']
        : ['Book a flight', 'check your flight Status', 'Ask general queries'];
      const text = selectedCountry?.language === 'AR' 
        ? 'إليك بعض الاستفسارات الشائعة لتجربتها'
        : 'Here are some popular prompts for you to try';
      
      setChatMessages([{
        id: `msg-${Date.now()}`,
        sender: 'Assistant',
        text,
        isPromptBubble: true,
        isInitialPrompt: true,
        prompts
      }]);
    }
  }, [chatMessages.length, selectedCountry?.language]);



  const handleClose = useCallback(() => {
    setIsOpen(false);
    setIsMinimized(false);
    setIsFullscreen(false);
    // Auto-open after 2 seconds
    setTimeout(() => {
      setIsOpen(true);
    }, 2000);
  }, []);



  const handleFeedback = async (messageIndex, type, rating = null, comment = '') => {
    setFeedbackStates(prev => ({
      ...prev,
      [messageIndex]: {
        ...prev[messageIndex],
        type,
        rating,
        comment,
        showForm: type === 'feedback'
      }
    }));
    
    // Save feedback to backend
    if (type === 'like' || type === 'dislike') {
      try {
        await chatAPI.submitFeedback({
          message_id: chatMessages[messageIndex]?.id,
          feedback_type: type,
          session_id: sessionId
        });
      } catch (error) {
        console.error('Error saving feedback:', error);
      }
    }
  };

  const submitFeedback = async (messageIndex) => {
    const feedback = feedbackStates[messageIndex];
    
    // Save detailed feedback to backend
    try {
      await chatAPI.submitFeedback({
        message_id: chatMessages[messageIndex]?.id,
        feedback_type: feedback.rating ? 'rating' : 'comment',
        rating: feedback.rating,
        comment: feedback.comment,
        session_id: sessionId
      });
    } catch (error) {
      console.error('Error saving detailed feedback:', error);
    }
    
    setFeedbackStates(prev => ({
      ...prev,
      [messageIndex]: {
        ...prev[messageIndex],
        showForm: false,
        submitted: true,
        showThankYou: true
      }
    }));
    
    // Hide thank you message after 2 seconds
    setTimeout(() => {
      setFeedbackStates(prev => ({
        ...prev,
        [messageIndex]: {
          ...prev[messageIndex],
          showThankYou: false
        }
      }));
    }, 2000);
  };

  const handleEditMessage = (messageIndex, messageText) => {
    setEditingMessage(messageIndex);
    setEditText(messageText);
  };

  const saveEditedMessage = async () => {
    if (!editText.trim()) return;
    
    // Set loading state for button
    setIsEditLoading(true);
    
    // Close edit dialog first
    setEditingMessage(null);
    setEditText('');
    
    // Update the original message and replace assistant response with typing
    setChatMessages(prev => {
      const updated = [...prev];
      updated[editingMessage] = {
        ...updated[editingMessage],
        text: editText,
        isEdited: true
      };
      // Find and replace the next assistant response with typing indicator
      const nextAssistantIndex = updated.findIndex((msg, idx) => 
        idx > editingMessage && msg.sender === 'Assistant'
      );
      if (nextAssistantIndex !== -1) {
        updated[nextAssistantIndex] = { 
          id: `typing-${Date.now()}`, 
          sender: 'Assistant', 
          text: '', 
          isTyping: true 
        };
      }
      return updated;
    });
    
    // Get context from backend and send the edited message
    setIsLoading(true);
    try {
      // Fetch user context from backend using axios
      let backendContext = null;
      try {
        const contextResponse = await chatAPI.getContext(sessionId);
        backendContext = contextResponse.data;
      } catch (err) {
        console.log('Could not fetch backend context:', err);
      }

      const response = await chatAPI.sendMessage({
        user: { id: sessionId, name: 'Demo User' },
        query: editText,
        conversation_history: conversationHistory,
        country: selectedCountry?.code,
        context: {
          // Backend context (from other websites)
          backendContext: backendContext
        }
      });
      
      const data = response.data;
      
      // Update session and history
      if (data.session_id) setSessionId(data.session_id);
      if (data.conversation_history) setConversationHistory(data.conversation_history);
      
      setChatMessages(prev => {
        const updated = [...prev];
        // Find and replace the typing indicator with new response
        const typingIndex = updated.findIndex(msg => msg.isTyping);
        if (typingIndex !== -1) {
          const isHtmlCard = data.html_card && data.html_card.trim().length > 0;
          updated[typingIndex] = {
            id: `msg-${Date.now()}`,
            sender: 'Assistant',
            text: data.response || 'Sorry, I couldn\'t process your request.',
            agent: data.agent || 'unknown',
            images: data.images,
            html_card: data.html_card,
            isHtmlCard: isHtmlCard,
            isPromptBubble: true,
            prompts: data.related_queries || [],
            related_queries: data.related_queries,
            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          };
        }
        return updated;
      });
    } catch (err) {
      console.error('Edit message error:', err);
      setChatMessages(prev => {
        const updated = [...prev];
        // Find and replace the typing indicator with error message
        const typingIndex = updated.findIndex(msg => msg.isTyping);
        if (typingIndex !== -1) {
          updated[typingIndex] = {
            id: `msg-${Date.now()}`,
            sender: 'Assistant',
            text: `Sorry, there was an error processing your edited message: ${err.message}. Please try again.`,
            isPromptBubble: true,
            prompts: ['Try again', 'Contact support']
          };
        }
        return updated;
      });
    } finally {
      setIsLoading(false);
      setIsEditLoading(false);
    }
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    console.log('Selected country:', country);
    
    // Update initial prompts based on language
    if (country.language === 'AR') {
      setChatMessages([
        {
          id: `msg-${Date.now()}`,
          sender: 'Assistant',
          text: 'إليك بعض الاستفسارات الشائعة لتجربتها',
          isPromptBubble: true,
          isInitialPrompt: true,
          prompts: ['عرض رحلات إلى دبي', 'ما هو أفضل مكان للزيارة في ديسمبر', 'تحقق من حالة الرحلة']
        },
      ]);
    } else {
      setChatMessages([
        {
          id: `msg-${Date.now()}`,
          sender: 'Assistant',
          text: 'Here are some popular prompts for you to try',
          isPromptBubble: true,
          isInitialPrompt: true,
          prompts: ['Book a flight', 'check your flight Status', 'Ask general queries']
        },
      ]);
    }
  };

  const handleCopyText = useCallback((text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Show brief success indication
      console.log('Text copied to clipboard');
    }).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  }, []);

  const handleMagicAssistant = (messageText, messageIndex) => {
    // Always show loading popup first for 2 seconds
    setMagicLoading(messageIndex);
    
    setTimeout(() => {
      setMagicLoading(null);
      
      const userMessageId = chatMessages[messageIndex]?.id;
      const magicKey = `magic_${userMessageId}`;
      
      if (magicCache[magicKey] && magicCache[magicKey].queries) {
        setMagicPopup({
          show: true,
          queries: magicCache[magicKey].queries,
          messageIndex,
          responses: magicCache[magicKey].responses || []
        });
      } else {
        console.log('No magic cache found for key:', magicKey);
        setMagicPopup({
          show: true,
          queries: ['Ask another question', 'Get more info', 'Contact support'],
          messageIndex,
          responses: []
        });
      }
    }, 2000);
  };

  return (
    <>
      {/* Country/Language Selector - Top Right */}
      {/* <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 2000
      }}>
        <CountrySelector 
          onCountrySelect={handleCountrySelect}
          onClose={() => {}}
        />
      </div> */}
      

      
      <style>
        {`
          body {
            background: #071B2B !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          
          input::placeholder {
            color: #4a5568 !important;
            opacity: 1 !important;
            font-family: Roboto !important;
            font-weight: 600 !important;
            font-size: 16px !important;
            line-height: 100% !important;
            letter-spacing: 0% !important;
          }
          
          .chatbot-fixed-container {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            margin: 0 !important;
            font-size: 16px !important;
            transform: none !important;
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.6; }
          }
          
          @keyframes audioWave {
            0%, 100% { height: 8px; }
            50% { height: 16px; }
          }
          
          @keyframes typingDot {
            0%, 60%, 100% {
              transform: translateY(0);
              opacity: 0.4;
            }
            30% {
              transform: translateY(-10px);
              opacity: 1;
            }
          }
          
          .magic-spinner {
            animation: spin 0.5s linear infinite !important;
          }
          
          .magic-spinner-large {
            animation: spin 0.8s linear infinite, pulse 1.5s ease-in-out infinite !important;
          }
          
          /* Input field styling */
          input, textarea, input[type="text"] {
            background: #374151 !important;
            background-color: #374151 !important;
            color: white !important;
            border: none !important;
          }
          
          /* Input container styling */
          div[style*="#ffffff"] {
            background: #374151 !important;
          }
          
          /* Override any white backgrounds in input area */
          * {
            background-color: inherit;
          }
          
          input {
            background-color: #374151 !important;
          }
          
          /* Highest priority input styling */
          input[style] {
            background: #374151 !important;
            background-color: #374151 !important;
            color: white !important;
            border: none !important;
            outline: none !important;
          }
          
          /* Remove all borders and ensure grey background */
          input, input:focus, input:active {
            background: #374151 !important;
            background-color: #374151 !important;
            border: none !important;
            outline: none !important;
            box-shadow: none !important;
          }
          
          /* Target only input area elements */
          input {
            background: #374151 !important;
            background-color: #374151 !important;
          }
          
          /* Input container with white background */
          div[style*="background: '#ffffff'"], div[style*="background:#ffffff"], div[style*="background: white"] {
            background: #374151 !important;
          }
          
          /* Bottom input area */
          div[style*="padding: '12px 16px'"] {
            background: #374151 !important;
          }
          
          /* Remove input field borders and make it blend */
          input, input:focus, input:hover, input:active {
            border: none !important;
            outline: none !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            background: #374151 !important;
            background-color: #374151 !important;
          }
          
          /* Override any inline styles */
          input[style*="background"] {
            background: #374151 !important;
          }
          
          /* Target the specific input container */
          div[style*="background-color: rgba(255, 255, 255, 0.8)"] {
            background-color: #374151 !important;
            border: none !important;
            margin-top: 20px !important;
          }
          
          /* Target input container with border-radius */
          div[style*="border-radius: 9999px"] {
            margin-top: 30px !important;
            transform: translateY(20px) !important;
          }
          
          /* Target input with transparent background */
          input[style*="background-color: transparent"] {
            background-color: #374151 !important;
            color: white !important;
          }
          
          /* Make all action buttons visible */
          button {
            opacity: 1 !important;
            visibility: visible !important;
            display: flex !important;
          }
          
          /* Style magic and edit buttons specifically */
          button[title*="Magic"], button[title*="Edit"] {
            background: none !important;
            border: none !important;
            padding: 2px !important;
            z-index: 9999 !important;
            position: relative !important;
          }
          
          /* Ensure magic assistant area is visible */
          div[style*="gap: 4px"] {
            display: flex !important;
            opacity: 1 !important;
            visibility: visible !important;
          }
          
          /* Fix input area positioning */
          div[style*="position: fixed"][style*="bottom: 0"] {
            padding-bottom: 20px !important;
            height: auto !important;
          }
          
          /* Ensure buttons below input are visible */
          .chatbot-fixed-container > div:last-child {
            margin-bottom: 20px !important;
            padding-bottom: 20px !important;
          }
          
          /* Ensure input area is above message content */
          div[style*="position: fixed"][style*="bottom"] {
            z-index: 10000 !important;
            background: #071B2B !important;
          }
          
          /* Add margin to message area to prevent overlap */
          div[style*="overflowY: 'auto'"] {
            margin-bottom: 80px !important;
            padding-bottom: 20px !important;
          }
          
          /* Input container background */
          div[style*="background: '#ffffff'"] {
            background: #374151 !important;
          }
          
          /* Ensure input area buttons stay visible */
          div[style*="position: fixed"][style*="bottom"] button {
            z-index: 10001 !important;
            position: relative !important;
            display: flex !important;
            opacity: 1 !important;
          }
          
          /* Prevent message content from overlapping input buttons */
          div[style*="margin-top: -30px"] {
            margin-bottom: 60px !important;
          }
          
          /* Force input area to stay on top and move it down */
          div[style*="position: fixed"][style*="bottom: 0"] {
            z-index: 99999 !important;
            background: #071B2B !important;
            padding-top: 10px !important;
            bottom: 20px !important;
          }
          
          /* Add space at bottom of messages */
          div[style*="flex: 1"][style*="overflowY: auto"] {
            padding-bottom: 150px !important;
            margin-bottom: 50px !important;
          }
          
          /* Force all prompt buttons to be visible */
          button[style*="background: '#374151'"], button[style*="background:#374151"] {
            display: flex !important;
            visibility: visible !important;
            opacity: 1 !important;
            z-index: 10000 !important;
            margin-bottom: 8px !important;
          }
          
          /* Ensure user message bubbles stay on top */
          div[style*="background: '#ffffff'"] {
            z-index: 1000 !important;
            position: relative !important;
          }
          
          /* Assistant messages should not overlap user messages */
          div[style*="background: linear-gradient"] {
            z-index: 999 !important;
            position: relative !important;
          }
          
          /* Position prompt buttons directly below user messages */
          div[style*="background: '#ffffff'"] + div {
            margin-top: -40px !important;
            z-index: 1001 !important;
            position: relative !important;
          }
          
          /* Make magic assistant button container visible */
          div[style*="display: flex; justify-content: flex-end; gap: 2px;"] {
            display: flex !important;
            visibility: visible !important;
            opacity: 1 !important;
            z-index: 10002 !important;
            position: relative !important;
            background: transparent !important;
            padding: 4px !important;
            border-radius: 4px !important;
            margin-top: -35px !important;
          }
          
          /* Ensure assistant response messages are fully visible */
          div[style*="margin-top: -30px"] {
            margin-top: 0px !important;
            margin-bottom: 20px !important;
            overflow: visible !important;
          }
          
          /* Make sure response content is not clipped */
          div[style*="background: transparent"] {
            overflow: visible !important;
            height: auto !important;
            max-height: none !important;
            color: white !important;
            background: none !important;
          }
          
          /* Remove background from assistant response bubbles */
          div[style*="background: linear-gradient"] {
            background: none !important;
          }
          
          /* Make ALL response text white - comprehensive rules */
          div[style*="color: rgb(51, 51, 51)"] {
            color: white !important;
          }
          
          div[style*="color: rgb(102, 102, 102)"] {
            color: white !important;
          }
          
          div[style*="color: rgb(31, 41, 55)"] {
            color: white !important;
          }
          
          div[style*="color: rgb(6, 28, 44)"] {
            color: white !important;
          }
          
          /* Target all text content in assistant responses */
          .chatbot-fixed-container div:not([style*="background: rgb(255, 255, 255)"]) div {
            color: white !important;
          }
          
          /* Force white text for all response content */
          .chatbot-fixed-container div[style*="text-align: left"] {
            color: white !important;
          }
          
          /* Target span elements specifically */
          span[style*="color: rgb(51, 51, 51)"] {
            color: white !important;
          }
          
          span[style*="color: rgb(102, 102, 102)"] {
            color: white !important;
          }
          
          /* Force all spans in assistant responses to be white */
          .chatbot-fixed-container div:not([style*="background: rgb(255, 255, 255)"]) span {
            color: white !important;
          }
          
          /* Exception for country selector - must come before universal rule */
          .country-language-selector,
          .country-language-selector *,
          .selector-button,
          .selector-button *,
          .country-code,
          .separator,
          .language,
          .arrow {
            color: #333 !important;
            background: white !important;
          }
          
          /* More specific country selector override */
          .chatbot-fixed-container .country-language-selector * {
            color: #333 !important;
          }
          
          /* Highly specific override for country selector spans */
          .selector-button .country-code,
          .selector-button .separator,
          .selector-button .language,
          .selector-button .arrow {
            color: #333 !important;
            background: rgba(255, 255, 255, 0.9) !important;
          }
          
          /* Override the span rule specifically for country selector */
          .chatbot-fixed-container .selector-button span {
            color: #333 !important;
          }
          
          /* Universal rule for all text elements in responses - exclude country selector and links */
          .chatbot-fixed-container *:not(.country-language-selector):not(.country-language-selector *):not(.selector-button):not(.selector-button *):not(.country-code):not(.separator):not(.language):not(.arrow):not(a):not(a *) {
            color: white !important;
          }
          
          /* Keep source links in original blue color */
          .chatbot-fixed-container a[href] {
            color: rgb(0, 74, 151) !important;
            background-color: rgb(240, 248, 255) !important;
          }
          
          /* Keep SVG icons in source links original color */
          .chatbot-fixed-container a
          
          
          
          
          
          
          [href] svg {
            color: rgb(0, 74, 151) !important;
            fill: currentColor !important;
          }
          
          /* Maximum specificity override for country selector */
          body .chatbot-fixed-container .selector-button span.country-code,
          body .chatbot-fixed-container .selector-button span.separator,
          body .chatbot-fixed-container .selector-button span.language,
          body .chatbot-fixed-container .selector-button span.arrow {
            color: #000 !important;
            background: #fff !important;
            opacity: 1 !important;
          }
          
          /* Exception for user messages */
          .chatbot-fixed-container div[style*="background: rgb(255, 255, 255)"] * {
            color: white !important;
          }
          
          /* Only user query bubble should be grey - remove inner content styling */
          
          /* Change complete user message bubble to grey */
          div[style*="background: rgb(255, 255, 255)"] {
            background: #374151 !important;
            color: white !important;
          }
          
          /* Change user message text color to white */
          div[style*="background: rgb(255, 255, 255)"] div[style*="color: rgb(51, 51, 51)"] {
            color: white !important;
          }
        `}
      </style>


      {/* Chat Window */}
      {isOpen && (
        <div 
          ref={chatContainerRef}
          className={`chatbot-zoom-fix ${isFullscreen ? 'chatbot-fixed-container chatbot-fixed-fullscreen' : 'chatbot-fixed-container'}`}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            borderRadius: '0px',
            background: '#071B2B',
            boxShadow: 'none',
            border: 'none',
            backdropFilter: 'none',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 9999,
            fontFamily: '"Nunito Sans", sans-serif',
            opacity: 1
          }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: '16px 24px 12px 24px',
            paddingTop: '20px',

            borderRadius: '24px 24px 0 0'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                <div style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "20px",
                  backgroundColor: "#FFD700",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: "15px",
                  marginBottom: "12px"
                }}>
                  <img
                    src="/images_all/J9.svg"
                    alt="J9 AI Bot"
                    width={24}
                    height={24}
                    style={{ width: "24px", height: "24px" }}
                  />
                </div>
                <div style={{color: 'white', fontSize: 24, fontFamily: 'Avenir', fontWeight: '500', wordWrap: 'break-word', marginTop: '-5px'}}>
                  {selectedCountry?.language === 'AR' ? 'مرحباً، كيف\nيمكنني مساعدتك؟' : 'Hi, how\ncan I help you?'}
                </div>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '8px', 
                  alignItems: 'flex-start',
                  marginTop: '12px'
                }}>
                  {['Book a flight', 'check your flight Status', 'Ask general queries'].map((prompt, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setInput(prompt);
                        setTimeout(() => handleSend(), 100);
                      }}
                      style={{
                        display: 'inline-flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: 'fit-content',
                        height: 'auto',
                        minHeight: '43px',
                        gap: '10px',
                        borderRadius: '20px',
                        padding: '16px 20px',
                        background: '#374151',
                        boxShadow: '4px 4px 8px rgba(0, 0, 0, 0.12)',
                        color: '#FFFFFF',
                        fontFamily: 'Roboto',
                        fontWeight: '500',
                        fontSize: '18px',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      <img 
                        src="/images_all/Sparkle.png" 
                        alt="✨" 
                        style={{ width: '24px', height: '24px', marginRight: '8px' }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.textContent = `✨ ${prompt}`;
                        }}
                      />
                      {prompt}
                    </button>
                  ))}
                </div>

              </div>
              <h1 style={{
                width: '161px',
                height: '56px',
                fontFamily: 'Roboto',
                fontWeight: '600',
                fontSize: '24px',
                lineHeight: '100%',
                letterSpacing: '0%',
                background: 'linear-gradient(90deg, #004A97 0%, #3FB4E5 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                margin: 0,
                opacity: 1,
                textAlign: selectedCountry?.language === 'AR' ? 'right' : 'left',
                direction: selectedCountry?.language === 'AR' ? 'rtl' : 'ltr'
              }}>
</h1>

            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px', marginRight: '24px' }}>
              <div style={{ zIndex: 10001, marginTop: '12px' }}>
                <CountrySelector 
                  onCountrySelect={handleCountrySelect}
                  onClose={() => {}}
                />
              </div>
              <button onClick={() => {
                setChatMessages([]);
                // Generate new session ID for fresh conversation
                const newId = 'session-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
                setSessionId(newId);
                console.log('Chat refreshed with new session ID:', newId);
              }} style={{
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                width: '20px',
                height: '20px',
                opacity: 1
              }} title="Refresh Chatbot">
                <img 
                  src="/images_all/refresh2.png" 
                  alt="Refresh" 
                  style={{
                    width: '20px',
                    height: '20px'
                  }}
                />
              </button>
              <button onClick={handleClose} style={{
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                width: '20px',
                height: '20px',
                opacity: 1
              }} title="Close">
                <img 
                  src="/images_all/delete.png" 
                  alt="Close" 
                  style={{
                    width: '20px',
                    height: '20px'
                  }}
                />
              </button>
            </div>
          </div>



          {/* ChatGPT-Style Audio Player */}
          {isSpeaking && (
            <div style={{
              background: '#f7f7f8',
              borderBottom: '1px solid #e5e5e7',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              {/* Play/Pause Button */}
              <button
                onClick={() => {
                  if (currentAudio) {
                    if (isAudioPaused || currentAudio.paused) {
                      currentAudio.play();
                    } else {
                      currentAudio.pause();
                    }
                  } else if (speechSynthesis.speaking) {
                    if (speechSynthesis.paused) {
                      speechSynthesis.resume();
                      setIsAudioPaused(false);
                      // Resume progress interval
                      if (!progressInterval && audioDuration > 0) {
                        let currentTime = 0;
                        const newInterval = setInterval(() => {
                          currentTime += 0.1;
                          if (currentTime >= audioDuration) {
                            clearInterval(newInterval);
                            return;
                          }
                          setAudioProgress((currentTime / audioDuration) * 100);
                        }, 100);
                        setProgressInterval(newInterval);
                      }
                    } else {
                      speechSynthesis.pause();
                      setIsAudioPaused(true);
                      // Pause progress interval
                      if (progressInterval) {
                        clearInterval(progressInterval);
                        setProgressInterval(null);
                      }
                    }
                  }
                }}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: 'none',
                  background: '#004A97',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px'
                }}
                title={(isAudioPaused || speechSynthesis.paused) ? 'Resume' : 'Pause'}
              >
                {(isAudioPaused || speechSynthesis.paused) ? '▶️' : '⏸️'}
              </button>
              
              {/* Progress Bar Container */}
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                {/* Progress Bar */}
                <div style={{
                  flex: 1,
                  height: '3px',
                  backgroundColor: '#e5e5e7',
                  borderRadius: '2px',
                  position: 'relative',
                  marginRight: '8px'
                }}>
                  <div style={{
                    width: `${audioProgress}%`,
                    height: '100%',
                    backgroundColor: '#004A97',
                    borderRadius: '2px',
                    transition: 'width 0.1s ease'
                  }} />
                </div>
              </div>
              
              {/* Stop Button */}
              <button
                onClick={() => {
                  if (currentAudio) {
                    currentAudio.pause();
                    currentAudio.currentTime = 0;
                    setCurrentAudio(null);
                  }
                  if (progressInterval) {
                    clearInterval(progressInterval);
                    setProgressInterval(null);
                  }
                  speechSynthesis.cancel();
                  setIsSpeaking(false);
                  setCurrentSpeakingMessage(null);
                  setIsAudioPaused(false);
                  setAudioProgress(0);
                  setAudioDuration(0);
                }}
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '4px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  color: '#666',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px'
                }}
                title="Stop"
              >
                ⏹️
              </button>
            </div>
          )}

          {/* Messages */}
          {!isMinimized && (
            <div style={{
              flex: 1,
              padding: '0 24px',
              overflowY: 'auto',
              fontSize: '16px',
              scrollBehavior: 'smooth'
            }}>
              {/* Messages */}
              {chatMessages.map((msg, idx) => (
                <>
                <div key={idx} style={{
                  display: 'block',
                  textAlign: msg.sender === 'user' ? 'right' : 'left',
                  marginBottom: msg.sender === 'user' ? '50px' : ((msg.images || msg.isHtmlCard) && msg.prompts && msg.prompts.length > 0 ? '5px' : '10px'),
                  marginTop: msg.isPromptBubble ? '-30px' : '0',
                  direction: applyRTLStyling(msg.text) ? 'rtl' : 'ltr',
                  unicodeBidi: applyRTLStyling(msg.text) ? 'embed' : 'normal'
                }}>
                  <div style={{
                    background: msg.sender === 'user' 
                      ? '#ffffff' 
                      : (msg.isPromptBubble && msg.isInitialPrompt) ? 'transparent' : msg.isPromptBubble ? 'transparent' : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                    display: (msg.isPromptBubble && msg.isInitialPrompt) ? 'none' : 'block',
                    color: msg.sender === 'user' 
                      ? '#000000' 
                      : msg.isPromptBubble ? '#1f2937' : '#000000',
                    borderRadius: msg.sender === 'user' ? '24px 0px 24px 24px' : (msg.isPromptBubble ? '0px 20px 20px 20px' : '18px'),
                    padding: msg.isPromptBubble 
                      ? '12px 16px 12px 16px' 
                      : (isFullscreen ? '18px 22px' : '12px 16px'),
                    width: msg.sender === 'user' ? 'fit-content' : 'auto',
                    height: msg.isPromptBubble ? 'auto' : 'auto',
                    maxWidth: msg.sender === 'user' 
                      ? 'fit-content' 
                      : msg.isPromptBubble 
                        ? '400px' 
                        : (isFullscreen ? '85%' : '80%'),
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    boxShadow: msg.sender === 'user' 
                      ? '0 2px 8px rgba(0, 0, 0, 0.1)' 
                      : msg.isPromptBubble ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                    border: msg.sender === 'user' ? 'none' : (msg.isPromptBubble ? 'none' : '1px solid rgba(71, 85, 105, 0.1)'),
                    borderImage: 'none',
                    backdropFilter: 'none',
                    animation: 'messageSlideIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    lineHeight: '1.5',
                    fontSize: '16px',
                    fontWeight: 'normal',
                    position: 'relative',
                    margin: msg.sender === 'user' ? '0 0 0 auto' : '0',
                    textAlign: applyRTLStyling(msg.text) ? 'right' : 'left',
                    direction: applyRTLStyling(msg.text) ? 'rtl' : 'ltr',
                    unicodeBidi: applyRTLStyling(msg.text) ? 'embed' : 'normal'
                  }}>
                    {msg.isHtmlCard ? (
                      <div 
                        dangerouslySetInnerHTML={{ __html: msg.html_card }} 
                        style={{
                          fontSize: '14px',
                          lineHeight: '1.4'
                        }}
                      />
                    ) : msg.isInitialPrompt ? (
                      <>
                        <p className="prompt-text" style={{
                          width: '280px',
                          height: '38px',
                          opacity: 1,
                          transform: 'rotate(0deg)',
                          fontFamily: 'Roboto',
                          fontWeight: '500',
                          fontStyle: 'normal',
                          fontSize: '16px',
                          lineHeight: '100%',
                          letterSpacing: '0%',
                          display: 'flex',
                          alignItems: 'center',
                          color: '#061C2C',
                          margin: '0',
                          textAlign: 'left'
                        }}>
                          {msg.text}
                        </p>

                      </>
                    ) : msg.isTyping ? (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '4px 0'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#004a97',
                            animation: 'typingDot 1.4s infinite ease-in-out',
                            animationDelay: '0s'
                          }} />
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#004a97',
                            animation: 'typingDot 1.4s infinite ease-in-out',
                            animationDelay: '0.2s'
                          }} />
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#004a97',
                            animation: 'typingDot 1.4s infinite ease-in-out',
                            animationDelay: '0.4s'
                          }} />
                        </div>
                        <span style={{
                          color: '#64748b',
                          fontSize: '14px',
                          fontStyle: 'italic'
                        }}>
                          Assistant is typing...
                        </span>
                      </div>
                    ) : (
                      <div style={{
                        width: '100%',
                        textAlign: 'left',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word'
                      }}>
                        {(() => {
                          // Check if message contains [FLIGHT_CARDS] tag or Arabic flight content
                          if (msg.text.includes('[FLIGHT_CARDS]') || (msg.text.includes('[ARABIC_RTL]') && msg.text.includes('رحلة'))) {
                            const isArabic = msg.text.includes('[ARABIC_RTL]');
                            const textWithoutTag = msg.text.replace('[FLIGHT_CARDS]', '').replace('[ARABIC_RTL]', '').trim();
                            // Parse flights from structured text - avoid duplicates
                            const flights = [];
                            const flightSections = textWithoutTag.split(/(?=Flight\s+\d+:|رحلة\s+\d+:)/).filter(section => section.trim());
                            
                            // Process each unique flight section
                            flightSections.forEach((section, index) => {
                              const lines = section.split('\n').filter(line => line.trim());
                              if (lines.length === 0) return;
                              
                              const flightHeader = lines[0];
                              const flightNumberMatch = flightHeader.match(/(?:Flight|رحلة)\s+(\d+)/);
                              
                              if (flightNumberMatch) {
                                const flightNum = flightNumberMatch[1];
                                
                                // Check if we already have this flight number
                                if (!flights.find(f => f.number === flightNum)) {
                                  flights.push({
                                    number: flightNum,
                                    section: section,
                                    lines: lines
                                  });
                                }
                              }
                            });
                            
                            const flightMatches = flights.map(f => f.section);
                            
                            // Get text after [FLIGHT_CARDS] tag
                            const parts = msg.text.split('[FLIGHT_CARDS]');
                            const remainingText = parts.length > 1 ? parts[1].trim() : null;
                            
                            return (
                              <div>
                                <div style={{ marginBottom: '16px' }}>
                                  {textWithoutTag.split(/Flight\s+\d+:|رحلة\s+\d+:/)[0].trim()}
                                </div>
                                
                                {flightMatches && flightMatches.map((flight, flightIndex) => {
                                  const lines = flight.split('\n').filter(line => line.trim());
                                  const flightHeader = lines[0] || '';
                                  
                                  // Extract flight number and date from the cleaned data
                                  const flightNumberMatch = flightHeader.match(/(?:Flight|رحلة)\s+(\d+):\s*([^-]+)\s*-\s*(.+)/);
                                  const directFlightMatch = flightHeader.match(/(?:Flight|رحلة)\s+(\d+):\s*(J9\s*\d+)\s*-\s*(.+)/);
                                  let flightNumber = `${flightIndex + 1}`;
                                  let route = 'Route not available';
                                  let travelDate = '';
                                  
                                  if (directFlightMatch) {
                                    // Direct match for "Flight 1: J9 121 - KWI to DXB"
                                    flightNumber = directFlightMatch[2].replace(/\s+/g, ' ');
                                    route = directFlightMatch[3] || 'Route not available';
                                  } else if (flightNumberMatch) {
                                    flightNumber = `J9 ${flightNumberMatch[1]}`;
                                    route = flightNumberMatch[3] || 'Route not available';
                                  } else {
                                    // Fallback: try to extract from header
                                    const simpleMatch = flightHeader.match(/(?:Flight|رحلة)\s+(\d+)/);
                                    if (simpleMatch) {
                                      flightNumber = `J9 ${simpleMatch[1]}`;
                                    }
                                    const routeMatch = flightHeader.split(': ')[1];
                                    if (routeMatch) route = routeMatch;
                                  }
                                  
                                  // Extract travel date from lines
                                  for (let i = 1; i < lines.length; i++) {
                                    const line = lines[i];
                                    if (line.includes('Date:') || line.includes('التاريخ:')) {
                                      travelDate = line.split(':')[1]?.trim() || '';
                                      break;
                                    }
                                  }
                                  
                                  // Handle both Arabic and English route formats
                                  let origin, destination;
                                  if (isArabic && route.includes('إلى')) {
                                    [origin, destination] = route.split(' إلى ');
                                  } else if (route.includes(' to ')) {
                                    [origin, destination] = route.split(' to ');
                                  } else {
                                    // Fallback parsing
                                    origin = 'KWI';
                                    destination = 'DXB';
                                  }
                                  
                                  const originCode = origin?.trim() || 'KWI';
                                  const destinationCode = destination?.trim() || 'DXB';
                                  
                                  // Map airport codes to city names
                                  const cityMap = {
                                    'KWI': { en: 'Kuwait', ar: 'الكويت' },
                                    'DXB': { en: 'Dubai', ar: 'دبي' },
                                    'BAH': { en: 'Bahrain', ar: 'البحرين' },
                                    'LHR': { en: 'London', ar: 'لندن' },
                                    'CDG': { en: 'Paris', ar: 'باريس' }
                                  };
                                  
                                  const originCity = cityMap[originCode]?.[isArabic ? 'ar' : 'en'] || originCode;
                                  const destinationCity = cityMap[destinationCode]?.[isArabic ? 'ar' : 'en'] || destinationCode;
                                  
                                  // Parse departure and arrival times more flexibly
                                  let departure = '--:--', arrival = '--:--';
                                  const timePattern = /\d{2}:\d{2}/g;
                                  
                                  for (let i = 1; i < lines.length; i++) {
                                    const line = lines[i];
                                    if (line.includes('Departure') || line.includes('المغادرة')) {
                                      const times = line.match(timePattern);
                                      if (times && times.length >= 1) {
                                        departure = times[0];
                                        if (times.length >= 2) arrival = times[1];
                                      }
                                      break;
                                    }
                                  }
                                  
                                  // Extract duration and fare options
                                  let duration = 'N/A';
                                  let fareOptions = '';
                                  for (let i = 1; i < lines.length; i++) {
                                    const line = lines[i];
                                    if (line.includes('Duration') || line.includes('مدة')) {
                                      duration = line.replace(/Duration:\s*|مدة الرحلة:\s*/g, '').trim();
                                    }
                                    if (line.includes('Fares:') || line.includes('الأسعار:')) {
                                      fareOptions = line.replace(/Fares:\s*|الأسعار:\s*/g, '').trim();
                                    }
                                  }
                                  
                                  // Extract starting price from fare options
                                  let price = 'Price not available';
                                  
                                  if (fareOptions) {
                                    // Get the first (usually cheapest) fare option
                                    const firstFare = fareOptions.split('|')[0];
                                    const fareMatch = firstFare.trim().match(/([\d.,]+)\s*(\w+)/);
                                    if (fareMatch) {
                                      price = `From ${fareMatch[1]} ${fareMatch[2]}`;
                                    }
                                  } else {
                                    // Try multiple price extraction methods as fallback
                                    const pricePatterns = [
                                      new RegExp(`Flight\s+${flightIndex + 1}[\s\S]*?([\d.,]+)\s*(KWD|AED|USD|EUR|GBP)`, 'i'),
                                      /([\d.,]+)\s*(KWD|AED|USD|EUR|GBP)/gi
                                    ];
                                    
                                    for (const pattern of pricePatterns) {
                                      if (pattern.global) {
                                        const allMatches = [...msg.text.matchAll(pattern)];
                                        if (allMatches[flightIndex] && allMatches[flightIndex][1]) {
                                          price = `From ${allMatches[flightIndex][1]} ${allMatches[flightIndex][2]}`;
                                          break;
                                        }
                                      } else {
                                        const match = msg.text.match(pattern);
                                        if (match && match[1]) {
                                          price = `From ${match[1]} ${match[2]}`;
                                          break;
                                        }
                                      }
                                    }
                                    
                                    // Fallback prices if no price found
                                    if (price === 'Price not available') {
                                      const fallbackPrices = ['From 150.00 KWD', 'From 200.00 KWD', 'From 180.00 KWD'];
                                      price = fallbackPrices[flightIndex] || 'From 150.00 KWD';
                                    }
                                  }
                                  
                                  // Extract class info
                                  let classInfo = 'Economy';
                                  for (let i = 1; i < lines.length; i++) {
                                    const line = lines[i];
                                    if (line.includes('Class') || line.includes('درجة')) {
                                      classInfo = line.replace(/Class:\s*|درجة:\s*/g, '').trim();
                                      break;
                                    }
                                  }
                                  

                                  
                                  // If no fare options found, create default ones
                                  if (!fareOptions) {
                                    const defaultFares = isArabic ? 
                                      ['اقتصادي 150 د.ك', 'بريميوم 250 د.ك', 'بزنس 450 د.ك'] :
                                      ['Economy 150 KWD', 'Premium 250 KWD', 'Business 450 KWD'];
                                    fareOptions = defaultFares.join(' | ');
                                  }
                                  
                                  return (
                                    <div key={flightIndex} style={{
                                      background: 'white',
                                      borderRadius: '12px',
                                      padding: '20px',
                                      color: '#333',
                                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                      border: '1px solid #e5e7eb',
                                      marginBottom: '16px'
                                    }}>
                                      <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '16px'
                                      }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                          <div style={{
                                            background: '#004A97',
                                            color: 'white',
                                            borderRadius: '8px',
                                            padding: '8px 12px',
                                            fontWeight: 'bold',
                                            fontSize: '16px'
                                          }}>
                                            {flightNumber}
                                          </div>
                                          <img 
                                            src="/images/general/jazeera.png" 
                                            alt="Jazeera Airways" 
                                            style={{ height: '20px', width: 'auto' }}
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                              e.target.nextSibling.style.display = 'inline';
                                            }}
                                          />
                                          <span style={{ fontSize: '14px', color: '#666', display: 'none' }}>Jazeera Airways</span>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                          {travelDate && (
                                            <div style={{ fontSize: '14px', color: '#666', fontWeight: '500' }}>{travelDate}</div>
                                          )}
                                        </div>
                                      </div>
                                      
                                      <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '12px'
                                      }}>
                                        <div style={{ textAlign: 'center' }}>
                                          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>{originCode}</div>
                                          <div style={{ fontSize: '12px', color: '#666' }}>{originCity}</div>
                                          <div style={{ fontSize: '14px', marginTop: '4px', color: '#333' }}>{departure}</div>
                                        </div>
                                        
                                        <div style={{
                                          flex: 1,
                                          display: 'flex',
                                          flexDirection: 'column',
                                          alignItems: 'center',
                                          margin: '0 20px'
                                        }}>
                                          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>{duration.split(' (')[0]}</div>
                                          <div style={{
                                            width: '100%',
                                            height: '2px',
                                            background: '#e5e7eb',
                                            position: 'relative'
                                          }}>
                                            <div style={{
                                              position: 'absolute',
                                              right: '0',
                                              top: '-4px',
                                              width: '8px',
                                              height: '8px',
                                              background: '#004A97',
                                              borderRadius: '50%'
                                            }}></div>
                                          </div>
                                          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Direct</div>
                                        </div>
                                        
                                        <div style={{ textAlign: 'center' }}>
                                          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>{destinationCode}</div>
                                          <div style={{ fontSize: '12px', color: '#666' }}>{destinationCity}</div>
                                          <div style={{ fontSize: '14px', marginTop: '4px', color: '#333' }}>{arrival}</div>
                                        </div>
                                      </div>
                                      
                                      <div style={{
                                        paddingTop: '12px',
                                        borderTop: '1px solid #e5e7eb'
                                      }}>
                                        <div style={{ 
                                          fontSize: '12px', 
                                          color: '#666', 
                                          marginBottom: '12px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '8px'
                                        }}>
                                          <span>{classInfo}</span>
                                          <span>•</span>
                                          <button
                                            onClick={() => {
                                              const cardKey = `${flightIndex}-${msg.id}`;
                                              setExpandedFareDetails(prev => ({
                                                ...prev,
                                                [cardKey]: !prev[cardKey]
                                              }));
                                            }}
                                            style={{
                                              background: 'none',
                                              border: 'none',
                                              color: '#004A97',
                                              cursor: 'pointer',
                                              textDecoration: 'underline',
                                              fontSize: '12px',
                                              padding: '0'
                                            }}
                                          >
                                            {expandedFareDetails[`${flightIndex}-${msg.id}`] ? (isArabic ? 'إخفاء التفاصيل' : 'Hide details') : (isArabic ? 'انظر التفاصيل' : 'See details')}
                                          </button>
                                        </div>
                                        
                                        {expandedFareDetails[`${flightIndex}-${msg.id}`] && (
                                          <div style={{
                                            background: '#f8fafc',
                                            borderRadius: '8px',
                                            padding: '12px',
                                            marginBottom: '12px',
                                            border: '1px solid #e2e8f0'
                                          }}>
                                            <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', color: '#004A97' }}>
                                              {isArabic ? 'معلومات الرحلة' : 'Flight Information'}
                                            </div>
                                            <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.4', textAlign: isArabic ? 'right' : 'left', direction: isArabic ? 'rtl' : 'ltr' }}>
                                              {isArabic ? (
                                                <>
                                                  • الأمتعة: حقيبة يد 7 كج + حقيبة مسجلة 30 كج متضمنة<br/>
                                                  • الطائرة: إيرباص A320 بـ 180 مقعد<br/>
                                                  • الوجبة: وجبات خفيفة متاحة للشراء<br/>
                                                  • الترفيه: ترفيه عبر الجهاز الشخصي<br/>
                                                  • تسجيل الوصول: تسجيل إلكتروني متاح 24 ساعة قبل المغادرة<br/>
                                                  • الإلغاء: تطبق رسوم حسب نوع التعريفة المختارة
                                                </>
                                              ) : (
                                                <>
                                                  • Baggage: 7KG cabin bag + 30KG checked baggage included<br/>
                                                  • Aircraft: Airbus A320 with 180 seats<br/>
                                                  • Meal: Light refreshments available for purchase<br/>
                                                  • Entertainment: Personal device entertainment<br/>
                                                  • Check-in: Online check-in available 24 hours before departure<br/>
                                                  • Cancellation: Fees apply based on fare type selected
                                                </>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        
                                        {/* Fare Selection Buttons - Always show fare options */}
                                        <div style={{
                                          display: 'grid',
                                          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                                          gap: '8px'
                                        }}>
                                          {fareOptions && fareOptions.split('|').map((fare, fareIndex) => {
                                            const fareMatch = fare.trim().match(/(.+?)\s+([\d.,]+)\s*(\w+)/);
                                            if (!fareMatch) return null;
                                            
                                            const fareName = fareMatch[1].trim();
                                            const fareAmount = fareMatch[2].trim();
                                            const fareCurrency = fareMatch[3].trim();
                                            const farePrice = `${fareAmount} ${fareCurrency}`;
                                            
                                            return (
                                              <button
                                                key={fareIndex}
                                                onClick={async () => {
                                                  const selectionMessage = {
                                                    id: `user-${Date.now()}`,
                                                    sender: 'user',
                                                    text: isArabic ? 
                                                      `تم الاختيار: رحلة ${flightNumber} - ${fareName} - ${farePrice}` :
                                                      `Selected: Flight ${flightNumber} - ${fareName} - ${farePrice}`,
                                                    timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                                  };
                                                  
                                                  setChatMessages(prev => [
                                                    ...prev, 
                                                    selectionMessage,
                                                    { id: `typing-${Date.now()}`, sender: 'Assistant', text: '', isTyping: true }
                                                  ]);
                                                  
                                                  try {
                                                    const response = await chatAPI.sendMessage({
                                                      user: { id: sessionId, name: 'Demo User' },
                                                      query: isArabic ? `احجز رحلة ${flightIndex + 1} ${fareName}` : `book flight ${flightIndex + 1} ${fareName}`,
                                                      conversation_history: conversationHistory,
                                                      country: selectedCountry?.code
                                                    });
                                                    
                                                    setChatMessages(prev => {
                                                      const updated = [...prev];
                                                      updated.pop();
                                                      updated.push({
                                                        id: `msg-${Date.now()}`,
                                                        text: response.data.response,
                                                        sender: 'Assistant',
                                                        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                                      });
                                                      return updated;
                                                    });
                                                  } catch (error) {
                                                    console.error('Flight booking error:', error);
                                                    setChatMessages(prev => {
                                                      const updated = [...prev];
                                                      updated.pop();
                                                      updated.push({
                                                        id: `msg-${Date.now()}`,
                                                        text: isArabic ? 'حدث خطأ في معالجة طلب الحجز. يرجى المحاولة مرة أخرى.' : 'Error processing booking request. Please try again.',
                                                        sender: 'Assistant',
                                                        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                                      });
                                                      return updated;
                                                    });
                                                  }
                                                }}
                                                style={{
                                                  background: '#004A97',
                                                  color: 'white',
                                                  border: 'none',
                                                  borderRadius: '6px',
                                                  padding: '8px 12px',
                                                  fontSize: '12px',
                                                  fontWeight: 'bold',
                                                  cursor: 'pointer',
                                                  textAlign: 'center',
                                                  transition: 'all 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                  e.target.style.background = '#0056b3';
                                                  e.target.style.transform = 'scale(1.02)';
                                                }}
                                                onMouseLeave={(e) => {
                                                  e.target.style.background = '#004A97';
                                                  e.target.style.transform = 'scale(1)';
                                                }}
                                              >
                                                <div style={{ fontSize: '11px', marginBottom: '2px' }}>{fareName}</div>
                                                <div style={{ fontSize: '12px', fontWeight: 'bold' }}>{farePrice}</div>
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                                
                                {/* Additional flight information as text */}
                                {remainingText && (
                                  <div style={{
                                    marginTop: '20px',
                                    fontSize: '14px',
                                    color: '#333',
                                    lineHeight: '1.5'
                                  }}>
                                    {remainingText.split('\n').map((line, lineIndex) => {
                                      // Main headings: *heading*
                                      if (line.startsWith('*') && line.endsWith('*') && !line.startsWith('**')) {
                                        return (
                                          <div key={lineIndex} style={{
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                            marginTop: lineIndex > 0 ? '20px' : '0',
                                            marginBottom: '12px',
                                            color: '#004A97',
                                            borderBottom: '2px solid #004A97',
                                            paddingBottom: '4px'
                                          }}>
                                            {line.replace(/\*/g, '')}
                                          </div>
                                        );
                                      }
                                      // Subheadings: **subheading**
                                      if (line.startsWith('**') && line.endsWith('**')) {
                                        return (
                                          <div key={lineIndex} style={{
                                            fontSize: '16px',
                                            fontWeight: 'bold',
                                            marginTop: lineIndex > 0 ? '16px' : '0',
                                            marginBottom: '8px',
                                            color: '#333'
                                          }}>
                                            {line.replace(/\*\*/g, '')}
                                          </div>
                                        );
                                      }
                                      // Bullet points with bold keywords
                                      if (line.startsWith('•')) {
                                        return (
                                          <div key={lineIndex} style={{
                                            marginLeft: '16px',
                                            marginBottom: '4px',
                                            color: '#666',
                                            lineHeight: '1.4'
                                          }}>
                                            {line.split('**').map((part, partIndex) => 
                                              partIndex % 2 === 1 ? 
                                                <span key={partIndex} style={{ fontWeight: 'bold', color: '#333' }}>{part}</span> : 
                                                part
                                            )}
                                          </div>
                                        );
                                      }
                                      // Regular text with bold keywords
                                      if (line.trim()) {
                                        return (
                                          <div key={lineIndex} style={{ marginBottom: '6px' }}>
                                            {line.split('**').map((part, partIndex) => 
                                              partIndex % 2 === 1 ? 
                                                <span key={partIndex} style={{ fontWeight: 'bold', color: '#333' }}>{part}</span> : 
                                                part
                                            )}
                                          </div>
                                        );
                                      }
                                      return null;
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          
                          // Check for seat map response
                          if (msg.isSeatMap || msg.text.includes('seat map information') || msg.text.includes('Aircraft: Airbus')) {
                            const seatData = msg.text;
                            const isArabic = applyRTLStyling(msg.text) || selectedCountry?.language === 'AR';
                            
                            // Extract seat information and currency
                            
                            // Detect currency from route information
                            const routeMatch = seatData.match(/Route:\s*(\w+)\s*\([^)]+\)\s*to\s*(\w+)/);
                            const currencyMap = {
                              'KWI': { symbol: 'د.ك', code: 'KWD' },
                              'Kuwait': { symbol: 'د.ك', code: 'KWD' },
                              'kuwait': { symbol: 'د.ك', code: 'KWD' },
                              'DXB': { symbol: 'د.إ', code: 'AED' },
                              'Dubai': { symbol: 'د.إ', code: 'AED' },
                              'dubai': { symbol: 'د.إ', code: 'AED' },
                              'BAH': { symbol: 'د.ب', code: 'BHD' },
                              'Bahrain': { symbol: 'د.ب', code: 'BHD' },
                              'bahrain': { symbol: 'د.ب', code: 'BHD' },
                              'LHR': { symbol: '£', code: 'GBP' },
                              'London': { symbol: '£', code: 'GBP' },
                              'london': { symbol: '£', code: 'GBP' },
                              'CDG': { symbol: '€', code: 'EUR' },
                              'Paris': { symbol: '€', code: 'EUR' },
                              'paris': { symbol: '€', code: 'EUR' }
                            };
                            
                            const origin = routeMatch ? routeMatch[1] : 'Kuwait';
                            const currency = currencyMap[origin] || currencyMap[origin.toLowerCase()] || { symbol: '$', code: 'USD' };
                            const currencySymbol = currency.symbol;
                            const currencyCode = currency.code;
                            
                            // Parse seat information dynamically from backend response
                            const lines = seatData.split('\n');
                            const seatRows = [];
                            const seatInfo = {};
                            let maxRow = 0;
                            
                            // Extract all seat information from response
                            lines.forEach(line => {
                              // Match patterns like "Row 1: 1A - Premium - $15, 1B - Standard - $10"
                              const rowMatch = line.match(/Row\s+(\d+)[:.]?\s*(.*)/);
                              if (rowMatch) {
                                const rowNum = parseInt(rowMatch[1]);
                                maxRow = Math.max(maxRow, rowNum);
                                const seatInfo_line = rowMatch[2];
                                
                                // Extract individual seats from the row
                                const seatMatches = seatInfo_line.match(/(\d+[A-F])\s*-\s*([^-]+)\s*-\s*([\d.,]+)/g);
                                if (seatMatches) {
                                  seatMatches.forEach(seatMatch => {
                                    const parts = seatMatch.match(/(\d+[A-F])\s*-\s*([^-]+)\s*-\s*([\d.,]+)/);
                                    if (parts) {
                                      seatInfo[parts[1]] = {
                                        type: parts[2].trim(),
                                        price: parseFloat(parts[3].replace(/,/g, ''))
                                      };
                                    }
                                  });
                                }
                              }
                              
                              // Also try simpler pattern: "1A $15, 1B $10"
                              const simpleSeatMatches = line.match(/(\d+[A-F])\s*[\$د.كد.إد.ب£€]([\d.,]+)/g);
                              if (simpleSeatMatches) {
                                simpleSeatMatches.forEach(match => {
                                  const parts = match.match(/(\d+[A-F])\s*[\$د.كد.إد.ب£€]([\d.,]+)/);
                                  if (parts) {
                                    const rowNum = parseInt(parts[1]);
                                    maxRow = Math.max(maxRow, rowNum);
                                    seatInfo[parts[1]] = {
                                      type: 'Standard',
                                      price: parseFloat(parts[2].replace(/,/g, ''))
                                    };
                                  }
                                });
                              }
                            });
                            
                            // If no seats found in response, use fallback data
                            if (Object.keys(seatInfo).length === 0) {
                              maxRow = 28; // Default for A320
                            }
                            
                            // Generate seat map based on extracted data
                            for (let row = 1; row <= maxRow; row++) {
                              const seats = ['A', 'B', 'C', 'D', 'E', 'F'].map(letter => {
                                const seatId = `${row}${letter}`;
                                const seat = seatInfo[seatId];
                                
                                let seatType = 'Standard';
                                let price = 0;
                                let color = '#e5e7eb';
                                
                                if (seat) {
                                  seatType = seat.type;
                                  price = seat.price;
                                  
                                  // Color based on seat type from backend
                                  if (seatType.toLowerCase().includes('premium')) color = '#fbbf24';
                                  else if (seatType.toLowerCase().includes('preferred')) color = '#60a5fa';
                                  else if (seatType.toLowerCase().includes('extra') || seatType.toLowerCase().includes('legroom')) color = '#34d399';
                                  else if (price > 10) color = '#d1d5db';
                                  else color = '#f3f4f6';
                                } else {
                                  // Fallback logic if seat not in response
                                  if (row <= 3) { seatType = 'Premium'; color = '#fbbf24'; price = 15; }
                                  else if (row <= 10) { seatType = 'Preferred'; color = '#60a5fa'; price = 5; }
                                  else if (row === 11 || row === 12) { seatType = 'Extra Legroom'; color = '#34d399'; price = 12; }
                                  else if (row <= 20) { seatType = 'Standard'; color = '#d1d5db'; price = 3; }
                                  else { seatType = 'Standard'; color = '#f3f4f6'; price = 0; }
                                }
                                
                                return { 
                                  id: seatId, 
                                  letter, 
                                  price, 
                                  type: seatType, 
                                  color, 
                                  available: true, 
                                  tooltip: `${seatId} - ${seatType} - ${currencySymbol}${price}` 
                                };
                              });
                              seatRows.push({ row, seats });
                            }
                            
                            return (
                              <div>
                                <div style={{ 
                                  marginBottom: '16px', 
                                  fontSize: '16px', 
                                  fontWeight: 'bold', 
                                  color: '#004A97',
                                  textAlign: isArabic ? 'right' : 'left',
                                  direction: isArabic ? 'rtl' : 'ltr'
                                }}>
                                  ✈️ {isArabic ? 'خريطة المقاعد' : 'Seat Map'} - {origin} {isArabic ? 'إلى' : 'to'} {routeMatch ? routeMatch[2] : 'DXB'} (Airbus A320)
                                </div>
                                
                                {/* Legend */}
                                <div style={{ 
                                  marginBottom: '16px', 
                                  padding: '12px', 
                                  backgroundColor: '#f8fafc', 
                                  borderRadius: '8px',
                                  textAlign: isArabic ? 'right' : 'left',
                                  direction: isArabic ? 'rtl' : 'ltr'
                                }}>
                                  <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
                                    {isArabic ? 'أنواع المقاعد:' : 'Seat Types:'}
                                  </div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '12px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <div style={{ width: '12px', height: '12px', backgroundColor: '#fbbf24', borderRadius: '2px' }}></div>
                                      <span>{isArabic ? `بريميوم (${currencySymbol}11-15)` : `Premium (${currencySymbol}11-15)`}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <div style={{ width: '12px', height: '12px', backgroundColor: '#60a5fa', borderRadius: '2px' }}></div>
                                      <span>{isArabic ? `مفضل (${currencySymbol}3-5)` : `Preferred (${currencySymbol}3-5)`}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <div style={{ width: '12px', height: '12px', backgroundColor: '#34d399', borderRadius: '2px' }}></div>
                                      <span>{isArabic ? `مساحة إضافية (${currencySymbol}11-13)` : `Extra Legroom (${currencySymbol}11-13)`}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <div style={{ width: '12px', height: '12px', backgroundColor: '#d1d5db', borderRadius: '2px' }}></div>
                                      <span>{isArabic ? `عادي (${currencySymbol}2-3)` : `Standard (${currencySymbol}2-3)`}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <div style={{ width: '12px', height: '12px', backgroundColor: '#f3f4f6', borderRadius: '2px' }}></div>
                                      <span>{isArabic ? `خلفي (مجاني-${currencySymbol}2)` : `Rear (Free-${currencySymbol}2)`}</span>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Seat Map */}
                                <div style={{ 
                                  maxHeight: '400px', 
                                  overflowY: 'auto', 
                                  border: '1px solid #e5e7eb', 
                                  borderRadius: '8px',
                                  padding: '16px',
                                  backgroundColor: 'white'
                                }}>
                                  {seatRows.slice(0, 28).map(({ row, seats }) => (
                                    <div key={row} style={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      marginBottom: '4px',
                                      fontSize: '10px'
                                    }}>
                                      <div style={{ width: '25px', fontSize: '10px', color: '#666' }}>{row}</div>
                                      
                                      {/* Left side seats A, B, C */}
                                      {seats.slice(0, 3).map(seat => (
                                        <div key={seat.id} style={{
                                          width: '24px',
                                          height: '20px',
                                          margin: '1px',
                                          backgroundColor: seat.color,
                                          border: '1px solid #ccc',
                                          borderRadius: '4px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: '8px',
                                          fontWeight: 'bold',
                                          cursor: 'pointer',
                                          title: seat.tooltip
                                        }}>
                                          {seat.letter}
                                        </div>
                                      ))}
                                      
                                      {/* Aisle gap */}
                                      <div style={{ width: '12px' }}></div>
                                      
                                      {/* Right side seats D, E, F */}
                                      {seats.slice(3, 6).map(seat => (
                                        <div key={seat.id} style={{
                                          width: '24px',
                                          height: '20px',
                                          margin: '1px',
                                          backgroundColor: seat.color,
                                          border: '1px solid #ccc',
                                          borderRadius: '4px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: '8px',
                                          fontWeight: 'bold',
                                          cursor: 'pointer',
                                          title: seat.tooltip
                                        }}>
                                          {seat.letter}
                                        </div>
                                      ))}
                                    </div>
                                  ))}
                                </div>
                                
                                <div style={{ 
                                  marginTop: '12px', 
                                  fontSize: '12px', 
                                  color: '#666', 
                                  textAlign: 'center',
                                  direction: isArabic ? 'rtl' : 'ltr'
                                }}>
                                  {isArabic ? 
                                    `💡 مرر فوق المقاعد لرؤية الأسعار • A&F: نافذة • C&D: ممر • B&E: وسط • العملة: ${currencyCode}` :
                                    `💡 Hover over seats to see pricing • A&F: Window • C&D: Aisle • B&E: Middle • Currency: ${currencyCode}`
                                  }
                                </div>
                              </div>
                            );
                          }
                          
                          // Check for flight selection confirmation
                          if (msg.text.includes('Flight Selection Confirmed') || msg.text.includes('Your Selected Flight:')) {
                            const lines = msg.text.split('\n');
                            let flightNumber = '', route = '', departure = '', arrival = '', duration = '', price = '';
                            
                            lines.forEach(line => {
                              if (line.includes('Flight:')) flightNumber = line.split('Flight:')[1]?.trim() || '';
                              if (line.includes('Route:')) route = line.split('Route:')[1]?.trim() || '';
                              if (line.includes('Departure:')) departure = line.split('Departure:')[1]?.trim().split('T')[1]?.substring(0,5) || '';
                              if (line.includes('Arrival:')) arrival = line.split('Arrival:')[1]?.trim().split('T')[1]?.substring(0,5) || '';
                              if (line.includes('Duration:')) duration = line.split('Duration:')[1]?.trim() || '';
                              if (line.includes('Price:')) price = line.split('Price:')[1]?.trim() || '';
                            });
                            
                            const [originCode, destinationCode] = route.split(' to ');
                            const isArabic = applyRTLStyling(msg.text);
                            
                            return (
                              <div>
                                <div style={{ marginBottom: '16px' }}>
                                  Flight Selection Confirmed
                                </div>
                                
                                {/* Selected Flight Card */}
                                <div style={{
                                  background: 'white',
                                  borderRadius: '12px',
                                  padding: '20px',
                                  color: '#333',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                  border: '2px solid #10b981',
                                  marginBottom: '16px'
                                }}>
                                  <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '16px'
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                      <div style={{
                                        background: '#10b981',
                                        color: 'white',
                                        borderRadius: '8px',
                                        padding: '8px 12px',
                                        fontWeight: 'bold',
                                        fontSize: '16px'
                                      }}>
                                        ✓ J9 {flightNumber}
                                      </div>
                                      <span style={{ fontSize: '14px', color: '#10b981', fontWeight: 'bold' }}>
                                        {isArabic ? 'تم الاختيار' : 'SELECTED'}
                                      </span>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#004A97' }}>{price}</div>
                                    </div>
                                  </div>
                                  
                                  <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '12px'
                                  }}>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>{originCode}</div>
                                      <div style={{ fontSize: '14px', marginTop: '4px', color: '#333' }}>{departure}</div>
                                    </div>
                                    
                                    <div style={{
                                      flex: 1,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      margin: '0 20px'
                                    }}>
                                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>{duration}</div>
                                      <div style={{
                                        width: '100%',
                                        height: '2px',
                                        background: '#10b981',
                                        position: 'relative'
                                      }}>
                                        <div style={{
                                          position: 'absolute',
                                          right: '0',
                                          top: '-4px',
                                          width: '8px',
                                          height: '8px',
                                          background: '#10b981',
                                          borderRadius: '50%'
                                        }}></div>
                                      </div>
                                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Direct</div>
                                    </div>
                                    
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>{destinationCode}</div>
                                      <div style={{ fontSize: '14px', marginTop: '4px', color: '#333' }}>{arrival}</div>
                                    </div>
                                  </div>
                                </div>
                                
                                <div style={{ marginTop: '16px' }}>
                                  <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Great! Now I'd love to help you complete your booking.</div>
                                  <div style={{ marginBottom: '8px' }}>To proceed, I'll need some passenger information. Could you please share the following details?</div>
                                  <ul style={{ margin: '8px 0', paddingLeft: '20px', lineHeight: '1.6' }}>
                                    <li>First Name</li>
                                    <li>Last Name</li>
                                    <li>Gender (Male/Female)</li>
                                    <li>Date of Birth (YYYY-MM-DD)</li>
                                    <li>Email Address</li>
                                    <li>Phone Number</li>
                                  </ul>
                                  <div style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>You can also upload your passport using the buttons below for quick data entry! ✈️</div>
                                </div>
                              </div>
                            );
                          }
                          
                          // Check for passport upload buttons - remove all button tags from display text
                          if (msg.text.includes('[PASSPORT_UPLOAD_BUTTONS]') || msg.text.includes('[PASSPORT_UPLOAD_BUTTONS_AR]')) {
                            const cleanText = msg.text
                              .replace(/\[PASSPORT_UPLOAD_BUTTONS(_AR)?\]/g, '')
                              .replace(/\[PASSPORT_UPLOAD_BUTTONS\]/g, '')
                              .trim();
                            const isArabic = applyRTLStyling(cleanText) || selectedCountry?.language === 'AR';
                            
                            return (
                              <div>
                                <div style={{ 
                                  marginBottom: '12px',
                                  textAlign: isArabic ? 'right' : 'left',
                                  direction: isArabic ? 'rtl' : 'ltr'
                                }}>{cleanText}</div>
                              </div>
                            );
                          }
                          
                          // Check for selected flight card display
                          if (msg.selectedFlightCard) {
                            const card = msg.selectedFlightCard;
                            const isArabic = applyRTLStyling(msg.text);
                            
                            return (
                              <div>
                                {!msg.text.includes('Passenger Details Added Successfully') && !msg.text.includes('تم إضافة بيانات المسافر بنجاح') && (
                                  <div style={{ marginBottom: '16px' }}>
                                    {msg.text.replace(/\[ADDON_BUTTONS\]/g, '').trim()}
                                  </div>
                                )}
                                
                                {/* Selected Flight Card */}
                                <div style={{
                                  background: 'white',
                                  borderRadius: '12px',
                                  padding: '20px',
                                  color: '#333',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                  border: '2px solid #10b981',
                                  marginBottom: '16px'
                                }}>
                                  <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '16px'
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                      <div style={{
                                        background: '#10b981',
                                        color: 'white',
                                        borderRadius: '8px',
                                        padding: '8px 12px',
                                        fontWeight: 'bold',
                                        fontSize: '16px'
                                      }}>
                                        ✓ {card.flightNumber}
                                      </div>
                                      <span style={{ fontSize: '14px', color: '#10b981', fontWeight: 'bold' }}>
                                        {isArabic ? 'تم الاختيار' : 'SELECTED'}
                                      </span>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#004A97' }}>{card.price}</div>
                                    </div>
                                  </div>
                                  
                                  <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '12px'
                                  }}>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>{card.route.split(' to ')[0]}</div>
                                      <div style={{ fontSize: '14px', marginTop: '4px', color: '#333' }}>{card.departure}</div>
                                    </div>
                                    
                                    <div style={{
                                      flex: 1,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      margin: '0 20px'
                                    }}>
                                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>{card.duration}</div>
                                      <div style={{
                                        width: '100%',
                                        height: '2px',
                                        background: '#10b981',
                                        position: 'relative'
                                      }}>
                                        <div style={{
                                          position: 'absolute',
                                          right: '0',
                                          top: '-4px',
                                          width: '8px',
                                          height: '8px',
                                          background: '#10b981',
                                          borderRadius: '50%'
                                        }}></div>
                                      </div>
                                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Direct</div>
                                    </div>
                                    
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>{card.route.split(' to ')[1]}</div>
                                      <div style={{ fontSize: '14px', marginTop: '4px', color: '#333' }}>{card.arrival}</div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          
                          // Check for seat map
                          if (msg.text.includes('[SEAT_MAP]') && msg.text.includes('[/SEAT_MAP]')) {
                            const isArabic = msg.text.includes('[ARABIC_RTL]') || applyRTLStyling(msg.text) || selectedCountry?.language === 'AR';
                            const seatMapStart = msg.text.indexOf('[SEAT_MAP]') + '[SEAT_MAP]'.length;
                            const seatMapEnd = msg.text.indexOf('[/SEAT_MAP]');
                            const seatMapJson = msg.text.substring(seatMapStart, seatMapEnd);
                            const textBefore = msg.text.substring(0, msg.text.indexOf('[SEAT_MAP]')).replace('[ARABIC_RTL]', '').trim();
                            
                            let seatMapData;
                            try {
                              seatMapData = JSON.parse(seatMapJson);
                            } catch (e) {
                              return <div>{msg.text}</div>;
                            }
                            
                            const seatMaps = seatMapData?.data?.getSeatMapLayout?.seatMaps || [];
                            if (seatMaps.length === 0) {
                              return <div>{textBefore}</div>;
                            }
                            
                            const seatMap = seatMaps[0];
                            const cabins = seatMap?.cabins || [];
                            if (cabins.length === 0) {
                              return <div>{textBefore}</div>;
                            }
                            
                            const cabin = cabins[0];
                            const rows = cabin?.rows || {};
                            
                            return (
                              <div>
                                <div style={{ 
                                  marginBottom: '16px',
                                  textAlign: isArabic ? 'right' : 'left',
                                  direction: isArabic ? 'rtl' : 'ltr'
                                }}>
                                  {isArabic ? 
                                    'إليك خريطة المقاعد التفاعلية. انقر على أي مقعد متاح لاختياره:' : 
                                    "Here's your interactive seat map. Click any available seat to select it:"
                                  }
                                </div>
                                
                                {/* Aircraft Info */}
                                <div style={{
                                  background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                                  color: 'white',
                                  padding: '16px',
                                  borderRadius: '12px',
                                  marginBottom: '16px',
                                  textAlign: 'center',
                                  direction: isArabic ? 'rtl' : 'ltr'
                                }}>
                                  <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
                                    ✈️ {isArabic ? 'طائرة' : (seatMap.aircraft?.name || 'Aircraft')}
                                  </div>
                                  <div style={{ fontSize: '14px', opacity: 0.9 }}>
                                    {seatMap.origin} {isArabic ? '←' : '→'} {seatMap.destination} | {isArabic ? 'إجمالي المقاعد:' : 'Total Seats:'} {seatMap.aircraft?.totalNumberOfSeats || 0}
                                  </div>
                                </div>
                                
                                {/* Seat Map */}
                                <div style={{
                                  background: 'white',
                                  borderRadius: '12px',
                                  padding: '20px',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                  border: '1px solid #e5e7eb'
                                }}>
                                  <div style={{ 
                                    textAlign: 'center', 
                                    marginBottom: '16px', 
                                    fontSize: '16px', 
                                    fontWeight: 'bold',
                                    direction: isArabic ? 'rtl' : 'ltr'
                                  }}>
                                    {isArabic ? 'خريطة المقاعد' : 'Seat Map'}
                                  </div>
                                  
                                  {/* Legend */}
                                  <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: '16px',
                                    marginBottom: '20px',
                                    fontSize: '12px',
                                    direction: isArabic ? 'rtl' : 'ltr'
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <div style={{ width: '16px', height: '16px', background: '#10b981', borderRadius: '4px' }}></div>
                                      <span>{isArabic ? 'متاح' : 'Available'}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <div style={{ width: '16px', height: '16px', background: '#ef4444', borderRadius: '4px' }}></div>
                                      <span>{isArabic ? 'محجوز' : 'Occupied'}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <div style={{ width: '16px', height: '16px', background: '#f59e0b', borderRadius: '4px' }}></div>
                                      <span>{isArabic ? 'بريميوم' : 'Premium'}</span>
                                    </div>
                                  </div>
                                  
                                  {/* Seats Grid */}
                                  <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '4px',
                                    alignItems: 'center'
                                  }}>
                                    {Object.keys(rows).sort((a, b) => parseInt(a) - parseInt(b)).map(rowNum => {
                                      const row = rows[rowNum];
                                      const seats = row?.seats || {};
                                      
                                      return (
                                        <div key={rowNum} style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '4px'
                                        }}>
                                          {/* Row number */}
                                          <div style={{
                                            width: '24px',
                                            fontSize: '12px',
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            color: '#6b7280'
                                          }}>
                                            {rowNum}
                                          </div>
                                          
                                          {/* Seats A, B, C */}
                                          {['A', 'B', 'C'].map(letter => {
                                            const seatId = `${rowNum}${letter}`;
                                            const seat = seats[seatId];
                                            if (!seat) return <div key={letter} style={{ width: '24px', height: '24px' }}></div>;
                                            
                                            let seatColor = '#10b981'; // Available
                                            if (!seat.seatAvailable) seatColor = '#ef4444'; // Occupied
                                            else if (seat.seatType === 'PREM' || seat.seatType === 'EXLG') seatColor = '#f59e0b'; // Premium
                                            
                                            return (
                                              <div key={letter} style={{
                                                width: '24px',
                                                height: '24px',
                                                background: seatColor,
                                                borderRadius: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '10px',
                                                fontWeight: 'bold',
                                                color: 'white',
                                                cursor: seat.seatAvailable ? 'pointer' : 'not-allowed',
                                                border: seat.assignedTo ? '2px solid #3b82f6' : 'none'
                                              }}
                                              onClick={() => {
                                                if (seat.seatAvailable) {
                                                  const seatQuery = isArabic ? `اختر مقعد ${seatId}` : `select seat ${seatId}`;
                                                  const userMessage = {
                                                    id: `user-${Date.now()}`,
                                                    sender: 'user',
                                                    text: seatQuery,
                                                    timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                                  };
                                                  
                                                  setChatMessages(prev => [
                                                    ...prev,
                                                    userMessage,
                                                    { id: `typing-${Date.now()}`, sender: 'Assistant', text: '', isTyping: true }
                                                  ]);
                                                  
                                                  chatAPI.sendMessage({
                                                    user: { id: sessionId, name: 'Demo User' },
                                                    query: seatQuery,
                                                    conversation_history: conversationHistory,
                                                    country: selectedCountry?.code
                                                  }).then(response => {
                                                    setChatMessages(prev => {
                                                      const updated = [...prev];
                                                      updated.pop();
                                                      updated.push({
                                                        id: `msg-${Date.now()}`,
                                                        text: response.data.response,
                                                        sender: 'Assistant',
                                                        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                                      });
                                                      return updated;
                                                    });
                                                  }).catch(error => {
                                                    setChatMessages(prev => {
                                                      const updated = [...prev];
                                                      updated.pop();
                                                      updated.push({
                                                        id: `msg-${Date.now()}`,
                                                        text: isArabic ? `خطأ في اختيار المقعد ${seatId}. يرجى المحاولة مرة أخرى.` : `Error selecting seat ${seatId}. Please try again.`,
                                                        sender: 'Assistant',
                                                        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                                      });
                                                      return updated;
                                                    });
                                                  });
                                                }
                                              }}>
                                                {letter}
                                              </div>
                                            );
                                          })}
                                          
                                          {/* Aisle */}
                                          <div style={{ width: '16px' }}></div>
                                          
                                          {/* Seats D, E, F */}
                                          {['D', 'E', 'F'].map(letter => {
                                            const seatId = `${rowNum}${letter}`;
                                            const seat = seats[seatId];
                                            if (!seat) return <div key={letter} style={{ width: '24px', height: '24px' }}></div>;
                                            
                                            let seatColor = '#10b981'; // Available
                                            if (!seat.seatAvailable) seatColor = '#ef4444'; // Occupied
                                            else if (seat.seatType === 'PREM' || seat.seatType === 'EXLG') seatColor = '#f59e0b'; // Premium
                                            
                                            return (
                                              <div key={letter} style={{
                                                width: '24px',
                                                height: '24px',
                                                background: seatColor,
                                                borderRadius: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '10px',
                                                fontWeight: 'bold',
                                                color: 'white',
                                                cursor: seat.seatAvailable ? 'pointer' : 'not-allowed',
                                                border: seat.assignedTo ? '2px solid #3b82f6' : 'none'
                                              }}
                                              onClick={() => {
                                                if (seat.seatAvailable) {
                                                  const seatQuery = isArabic ? `اختر مقعد ${seatId}` : `select seat ${seatId}`;
                                                  const userMessage = {
                                                    id: `user-${Date.now()}`,
                                                    sender: 'user',
                                                    text: seatQuery,
                                                    timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                                  };
                                                  
                                                  setChatMessages(prev => [
                                                    ...prev,
                                                    userMessage,
                                                    { id: `typing-${Date.now()}`, sender: 'Assistant', text: '', isTyping: true }
                                                  ]);
                                                  
                                                  chatAPI.sendMessage({
                                                    user: { id: sessionId, name: 'Demo User' },
                                                    query: seatQuery,
                                                    conversation_history: conversationHistory,
                                                    country: selectedCountry?.code
                                                  }).then(response => {
                                                    setChatMessages(prev => {
                                                      const updated = [...prev];
                                                      updated.pop();
                                                      updated.push({
                                                        id: `msg-${Date.now()}`,
                                                        text: response.data.response,
                                                        sender: 'Assistant',
                                                        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                                      });
                                                      return updated;
                                                    });
                                                  }).catch(error => {
                                                    setChatMessages(prev => {
                                                      const updated = [...prev];
                                                      updated.pop();
                                                      updated.push({
                                                        id: `msg-${Date.now()}`,
                                                        text: isArabic ? `خطأ في اختيار المقعد ${seatId}. يرجى المحاولة مرة أخرى.` : `Error selecting seat ${seatId}. Please try again.`,
                                                        sender: 'Assistant',
                                                        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                                      });
                                                      return updated;
                                                    });
                                                  });
                                                }
                                              }}>
                                                {letter}
                                              </div>
                                            );
                                          })}
                                          
                                          {/* Row number */}
                                          <div style={{
                                            width: '24px',
                                            fontSize: '12px',
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            color: '#6b7280'
                                          }}>
                                            {rowNum}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  
                                  {/* Seat Types Info */}
                                  <div style={{
                                    marginTop: '20px',
                                    padding: '12px',
                                    background: '#f9fafb',
                                    borderRadius: '8px',
                                    fontSize: '12px',
                                    textAlign: isArabic ? 'right' : 'left',
                                    direction: isArabic ? 'rtl' : 'ltr'
                                  }}>
                                    <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                                      {isArabic ? 'أنواع المقاعد:' : 'Seat Types:'}
                                    </div>
                                    <div>• {isArabic ? 'PREM: مقاعد بريميوم مع راحة إضافية' : 'PREM: Premium seats with extra comfort'}</div>
                                    <div>• {isArabic ? 'EXLG: مقاعد بمساحة إضافية للأرجل' : 'EXLG: Extra legroom seats'}</div>
                                    <div>• {isArabic ? 'STD1/STD2: مقاعد اقتصادية عادية' : 'STD1/STD2: Standard economy seats'}</div>
                                    <div style={{ marginTop: '8px', fontStyle: 'italic' }}>
                                      {isArabic ? 'انقر على المقاعد المتاحة لاختيارها' : 'Click on available seats to select'}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          
                          // Check for passenger details card
                          if (msg.text.includes('Passenger Details Added Successfully') || msg.text.includes('تم إضافة بيانات المسافر بنجاح')) {
                            const isArabic = applyRTLStyling(msg.text) || msg.text.includes('تم إضافة بيانات المسافر بنجاح');
                            const lines = msg.text.split('\n');
                            
                            let passengerName = '', gender = '', dob = '', email = '', phone = '';
                            lines.forEach(line => {
                              if (line.includes('Passenger:') || line.includes('المسافر:')) {
                                passengerName = line.split(':')[1]?.trim() || '';
                              }
                              if (line.includes('Gender:') || line.includes('الجنس:')) {
                                gender = line.split(':')[1]?.trim() || '';
                              }
                              if (line.includes('Date of Birth:') || line.includes('تاريخ الميلاد:')) {
                                dob = line.split(':')[1]?.trim() || '';
                              }
                              if (line.includes('Email:') || line.includes('البريد الإلكتروني:')) {
                                email = line.split(':')[1]?.trim() || '';
                              }
                              if (line.includes('Phone:') || line.includes('رقم الهاتف:')) {
                                phone = line.split(':')[1]?.trim() || '';
                              }
                            });
                            
                            return (
                              <div>
                                <div style={{ marginBottom: '16px', fontSize: '16px', fontWeight: 'bold', color: '#10b981' }}>
                                  {isArabic ? '✅ تم إضافة بيانات المسافر بنجاح!' : '✅ Passenger Details Added Successfully!'}
                                </div>
                                
                                {/* Passenger Details Card */}
                                <div style={{
                                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fffe 100%)',
                                  borderRadius: '16px',
                                  padding: '24px',
                                  color: '#333',
                                  boxShadow: '0 8px 32px rgba(16, 185, 129, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)',
                                  border: '1px solid rgba(16, 185, 129, 0.2)',
                                  marginBottom: '20px',
                                  position: 'relative',
                                  overflow: 'hidden'
                                }}>
                                  {/* Background decoration */}
                                  <div style={{
                                    position: 'absolute',
                                    top: '-50px',
                                    right: '-50px',
                                    width: '100px',
                                    height: '100px',
                                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
                                    borderRadius: '50%',
                                    zIndex: 0
                                  }}></div>
                                  
                                  <div style={{ position: 'relative', zIndex: 1 }}>
                                    {/* Header */}
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '16px',
                                      marginBottom: '20px',
                                      paddingBottom: '16px',
                                      borderBottom: '1px solid rgba(16, 185, 129, 0.1)'
                                    }}>
                                      <div style={{
                                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                        color: 'white',
                                        borderRadius: '50%',
                                        width: '48px',
                                        height: '48px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '20px',
                                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                                      }}>
                                        👤
                                      </div>
                                      <div>
                                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#1f2937', marginBottom: '2px' }}>{passengerName}</div>
                                        <div style={{ fontSize: '14px', color: '#10b981', fontWeight: '500' }}>{isArabic ? 'بيانات المسافر' : 'Passenger Information'}</div>
                                      </div>
                                    </div>
                                    
                                    {/* Details Grid */}
                                    <div style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(2, 1fr)',
                                      gap: '16px',
                                      marginBottom: '16px'
                                    }}>
                                      <div style={{
                                        background: 'rgba(16, 185, 129, 0.05)',
                                        borderRadius: '12px',
                                        padding: '12px',
                                        border: '1px solid rgba(16, 185, 129, 0.1)'
                                      }}>
                                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{isArabic ? 'الجنس' : 'Gender'}</div>
                                        <div style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>{gender}</div>
                                      </div>
                                      <div style={{
                                        background: 'rgba(16, 185, 129, 0.05)',
                                        borderRadius: '12px',
                                        padding: '12px',
                                        border: '1px solid rgba(16, 185, 129, 0.1)'
                                      }}>
                                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{isArabic ? 'تاريخ الميلاد' : 'Date of Birth'}</div>
                                        <div style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>{dob}</div>
                                      </div>
                                    </div>
                                    
                                    {/* Contact Info */}
                                    <div style={{
                                      display: 'grid',
                                      gridTemplateColumns: '1fr',
                                      gap: '12px'
                                    }}>
                                      <div style={{
                                        background: 'rgba(16, 185, 129, 0.05)',
                                        borderRadius: '12px',
                                        padding: '12px',
                                        border: '1px solid rgba(16, 185, 129, 0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px'
                                      }}>
                                        <div style={{ fontSize: '16px' }}>📧</div>
                                        <div>
                                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{isArabic ? 'البريد الإلكتروني' : 'Email Address'}</div>
                                          <div style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>{email}</div>
                                        </div>
                                      </div>
                                      <div style={{
                                        background: 'rgba(16, 185, 129, 0.05)',
                                        borderRadius: '12px',
                                        padding: '12px',
                                        border: '1px solid rgba(16, 185, 129, 0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px'
                                      }}>
                                        <div style={{ fontSize: '16px' }}>📞</div>
                                        <div>
                                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{isArabic ? 'رقم الهاتف' : 'Phone Number'}</div>
                                          <div style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>{phone}</div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Render addon buttons */}
                                <div style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                                  gap: '8px',
                                  marginBottom: '16px'
                                }}>
                                  {(isArabic ? [
                                  { id: 'seatmap', name: '🗺️ خريطة المقاعد', api: 'seatmap' },
                                  { id: 'meals', name: '🍽️ الوجبات', api: 'meals' },
                                  { id: 'baggage', name: '🧳 خدمات إضافية', api: 'baggage' },
                                  { id: 'electronics', name: '📱 إلكترونيات', api: 'electronics' },
                                  { id: 'assistance', name: '♿ مساعدة خاصة', api: 'assistance' }
                                ] : [
                                  { id: 'seatmap', name: '🗺️ Seat Map', api: 'seatmap' },
                                  { id: 'meals', name: '🍽️ Meals', api: 'meals' },
                                  { id: 'baggage', name: '🧳 Extra Services', api: 'baggage' },
                                  { id: 'electronics', name: '📱 Electronics', api: 'electronics' },
                                  { id: 'assistance', name: '♿ Special Assistance', api: 'assistance' }
                                  ]).map(addon => (
                                    <button 
                                      key={addon.id}
                                      onClick={async () => {
                                        const queryText = addon.api === 'seatmap' ? 
                                          (isArabic ? 'عرض خريطة المقاعد' : 'show seat map') : 
                                          (isArabic ? `عرض خيارات ${addon.api}` : `Show ${addon.api} options`);
                                      const userMessage = {
                                        id: `user-${Date.now()}`,
                                        sender: 'user',
                                        text: queryText,
                                        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                      };
                                      setChatMessages(prev => [...prev, userMessage, { id: `typing-${Date.now()}`, sender: 'Assistant', text: '', isTyping: true }]);
                                      
                                      try {
                                        const response = await chatAPI.sendMessage({
                                          user: { id: sessionId, name: 'Demo User' },
                                          query: queryText,
                                          conversation_history: conversationHistory,
                                          country: selectedCountry?.code
                                        });
                                        
                                        setChatMessages(prev => {
                                          const updated = [...prev];
                                          updated.pop();
                                          updated.push({
                                            id: `msg-${Date.now()}`,
                                            text: response.data.response,
                                            sender: 'Assistant',
                                            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                          });
                                          return updated;
                                        });
                                      } catch (error) {
                                        setChatMessages(prev => {
                                          const updated = [...prev];
                                          updated.pop();
                                          updated.push({
                                            id: `msg-${Date.now()}`,
                                            text: 'Error loading addon options. Please try again.',
                                            sender: 'Assistant',
                                            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                          });
                                          return updated;
                                        });
                                      }
                                    }}
                                    style={{
                                      padding: '10px 8px',
                                      backgroundColor: '#f8f9fa',
                                      border: '2px solid #e9ecef',
                                      borderRadius: '8px',
                                      fontSize: '12px',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s',
                                      textAlign: 'center',
                                      fontWeight: '500'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = '#007bff';
                                      e.target.style.color = 'white';
                                      e.target.style.borderColor = '#007bff';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = '#f8f9fa';
                                      e.target.style.color = 'black';
                                      e.target.style.borderColor = '#e9ecef';
                                    }}
                                  >
                                    {addon.name}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                          
                          // Check for seat selection confirmation with button
                          if (msg.text.includes('[CONFIRM_SEAT_BUTTON]')) {
                            const cleanText = msg.text.replace('[CONFIRM_SEAT_BUTTON]', '').replace('[ARABIC_RTL]', '').trim();
                            const isArabic = msg.text.includes('[ARABIC_RTL]') || applyRTLStyling(cleanText) || selectedCountry?.language === 'AR';
                            
                            // Convert seat messages to Arabic if needed
                            let displayText = cleanText;
                            if (isArabic) {
                              if (cleanText.includes('selected successfully')) {
                                displayText = cleanText.replace(/Seat (\w+) selected successfully!/, 'تم اختيار المقعد $1 بنجاح!');
                              } else if (cleanText.includes('confirmed successfully')) {
                                displayText = cleanText.replace(/Seat (\w+) confirmed successfully!/, 'تم تأكيد المقعد $1 بنجاح!');
                              }
                            }
                            
                            return (
                              <div>
                                <div style={{ 
                                  marginBottom: '16px',
                                  textAlign: isArabic ? 'right' : 'left',
                                  direction: isArabic ? 'rtl' : 'ltr'
                                }}>{displayText}</div>
                                
                                <button
                                  onClick={async () => {
                                    const confirmQuery = isArabic ? 'تأكيد المقعد' : 'confirm seat';
                                    const userMessage = {
                                      id: `user-${Date.now()}`,
                                      sender: 'user',
                                      text: confirmQuery,
                                      timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                    };
                                    
                                    setChatMessages(prev => [
                                      ...prev,
                                      userMessage,
                                      { id: `typing-${Date.now()}`, sender: 'Assistant', text: '', isTyping: true }
                                    ]);
                                    
                                    try {
                                      const response = await chatAPI.sendMessage({
                                        user: { id: sessionId, name: 'Demo User' },
                                        query: confirmQuery,
                                        conversation_history: conversationHistory,
                                        country: selectedCountry?.code
                                      });
                                      
                                      setChatMessages(prev => {
                                        const updated = [...prev];
                                        updated.pop();
                                        updated.push({
                                          id: `msg-${Date.now()}`,
                                          text: response.data.response,
                                          sender: 'Assistant',
                                          timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                        });
                                        return updated;
                                      });
                                    } catch (error) {
                                      setChatMessages(prev => {
                                        const updated = [...prev];
                                        updated.pop();
                                        updated.push({
                                          id: `msg-${Date.now()}`,
                                          text: isArabic ? 'خطأ في تأكيد المقعد. يرجى المحاولة مرة أخرى.' : 'Error confirming seat. Please try again.',
                                          sender: 'Assistant',
                                          timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                        });
                                        return updated;
                                      });
                                    }
                                  }}
                                  style={{
                                    background: '#10b981',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '12px 24px',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                  }}
                                >
                                  {isArabic ? 'تأكيد المقعد' : 'Confirm Seat'}
                                </button>
                              </div>
                            );
                          }
                          
                          // Check for image tags in general responses
                          const imageMatch = msg.text.match(/\[IMAGE:([^\]]+)\]/);
                          const hasImage = imageMatch ? imageMatch[1] : null;
                          const textWithoutImage = hasImage ? msg.text.replace(/\[IMAGE:[^\]]+\]/g, '').trim() : msg.text;
                          
                          console.log('Image detection:', { imageMatch, hasImage, originalText: msg.text });
                          
                          // Remove [ARABIC_RTL] tag from display text and handle Arabic translations
                          let cleanTextWithoutImage = textWithoutImage.replace('[ARABIC_RTL]', '').trim();
                          const isArabicMessage = textWithoutImage.includes('[ARABIC_RTL]') || applyRTLStyling(cleanTextWithoutImage) || selectedCountry?.language === 'AR';
                          
                          // Convert seat messages to Arabic if needed
                          if (isArabicMessage) {
                            if (cleanTextWithoutImage.includes('selected successfully')) {
                              cleanTextWithoutImage = cleanTextWithoutImage.replace(/Seat (\w+) selected successfully!/, 'تم اختيار المقعد $1 بنجاح!');
                            } else if (cleanTextWithoutImage.includes('confirmed successfully')) {
                              cleanTextWithoutImage = cleanTextWithoutImage.replace(/Seat (\w+) confirmed successfully!/, 'تم تأكيد المقعد $1 بنجاح!');
                            }
                          }
                          
                          return (
                            <div>
                              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  {cleanTextWithoutImage.split('\n').map((line, lineIndex) => {
                                    // Main headings: *heading*
                                    if (line.startsWith('*') && line.endsWith('*') && !line.startsWith('**')) {
                                      return (
                                        <div key={lineIndex} style={{
                                          fontSize: '16px',
                                          fontWeight: 'bold',
                                          marginTop: lineIndex > 0 ? '16px' : '0',
                                          marginBottom: '12px',
                                          color: '#004A97',
                                          borderBottom: '2px solid #004A97',
                                          paddingBottom: '4px',
                                          textAlign: 'left'
                                        }}>
                                          {line.replace(/\*/g, '')}
                                        </div>
                                      );
                                    }
                                    // Subheadings: **subheading**
                                    if (line.startsWith('**') && line.endsWith('**')) {
                                      return (
                                        <div key={lineIndex} style={{
                                          fontSize: '14px',
                                          fontWeight: 'bold',
                                          marginTop: lineIndex > 0 ? '12px' : '0',
                                          marginBottom: '8px',
                                          color: '#333',
                                          textAlign: 'left'
                                        }}>
                                          {line.replace(/\*\*/g, '')}
                                        </div>
                                      );
                                    }
                                    // Bullet points and regular text with bold keywords
                                    if (line.trim()) {
                                      return (
                                        <div key={lineIndex} style={{
                                          marginBottom: '4px',
                                          marginLeft: line.startsWith('•') ? '16px' : '0',
                                          color: line.startsWith('•') ? '#666' : '#333',
                                          lineHeight: '1.4',
                                          textAlign: 'left'
                                        }}>
                                          {line.split('**').map((part, partIndex) => {
                                            if (partIndex % 2 === 1) {
                                              return <span key={partIndex} style={{ fontWeight: 'bold', color: '#333' }}>{part}</span>;
                                            }
                                            // Handle source links with proper regex
                                            const sourceRegex = /(\[Source:\s*https?:\/\/[^\]]+\])/g;
                                            const sourceParts = part.split(sourceRegex);
                                            return sourceParts.map((sourcePart, sourceIndex) => {
                                              const sourceMatch = sourcePart.match(/\[Source:\s*(https?:\/\/[^\]]+)\]/);
                                              if (sourceMatch) {
                                                const url = sourceMatch[1];
                                                return (
                                                  <a 
                                                    key={`${partIndex}-${sourceIndex}`}
                                                    href={url} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    style={{ 
                                                      display: 'inline-flex',
                                                      alignItems: 'center',
                                                      gap: '4px',
                                                      color: '#004a97', 
                                                      textDecoration: 'none', 
                                                      fontSize: '12px',
                                                      cursor: 'pointer',
                                                      padding: '4px 8px',
                                                      borderRadius: '6px',
                                                      backgroundColor: '#f0f8ff',
                                                      border: '1px solid #004a97',
                                                      fontWeight: '500'
                                                    }}
                                                    title={url}
                                                  >
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                                      <path d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z"/>
                                                    </svg>
                                                    Source
                                                  </a>
                                                );
                                              }
                                              return sourcePart;
                                            });
                                          })}
                                        </div>
                                      );
                                    }
                                    return null;
                                  })}
                                </div>
                                
                                {/* Display image on the right side inside bubble */}
                                {hasImage && (
                                  <div style={{
                                    width: '190px',
                                    flexShrink: 0,
                                    alignSelf: 'center',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center'
                                  }}>
                                    <img 
                                      src={chatAPI.getAgentImage('general', `${hasImage}.png`)}
                                      alt={hasImage}
                                      style={{
                                        width: '200px',
                                        height: '200px',
                                        objectFit: 'contain',
                                        objectPosition: 'center',
                                        borderRadius: '8px'
                                      }}
                                      onLoad={(e) => {
                                        console.log('Image loaded successfully:', e.target.src);
                                      }}
                                      onError={(e) => {
                                        console.error('Image failed to load:', e.target.src);
                                        e.target.style.display = 'none';
                                        // Show placeholder text instead
                                        const placeholder = e.target.nextSibling;
                                        if (placeholder) {
                                          placeholder.innerHTML = `📷 ${hasImage} image not found`;
                                          placeholder.style.color = '#999';
                                        }
                                      }}
                                    />

                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    
                    {/* Passport Upload Buttons - Outside message bubble */}
                    {msg.text.includes('[PASSPORT_UPLOAD_BUTTONS]') && (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '12px',
                        marginTop: '12px',
                        marginBottom: '8px'
                      }}>
                        <button
                          onClick={() => {
                            const isArabic = applyRTLStyling(msg.text) || selectedCountry?.language === 'AR';
                            window.lastActionWasPassport = true;
                            window.isArabicPassportFlow = isArabic;
                            fileInputRef.current.setAttribute('data-passport', 'true');
                            fileInputRef.current.click();
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 16px',
                            backgroundColor: '#004A97',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#0056b3';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#004A97';
                          }}
                        >
                          <span style={{ fontSize: '18px' }}>📤</span>
                          <span>{(applyRTLStyling(msg.text) || selectedCountry?.language === 'AR') ? 'رفع جواز السفر' : 'Upload Passport'}</span>
                        </button>
                        
                        <button
                          onClick={() => {
                            const isArabic = applyRTLStyling(msg.text) || selectedCountry?.language === 'AR';
                            window.isArabicPassportFlow = isArabic;
                            handleCameraCapture();
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 16px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#059669';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#10b981';
                          }}
                        >
                          <span style={{ fontSize: '18px' }}>📷</span>
                          <span>{(applyRTLStyling(msg.text) || selectedCountry?.language === 'AR') ? 'مسح جواز السفر' : 'Scan Passport'}</span>
                        </button>
                      </div>
                    )}
                    
                    {/* Action Cards for booking flow */}
                    {msg.actionCards && msg.actionCards.length > 0 && (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '8px',
                        marginTop: '12px',
                        marginBottom: '8px'
                      }}>
                        {msg.actionCards.map((card, cardIndex) => (
                          <button
                            key={cardIndex}
                            onClick={() => {
                              if (card.action === 'upload_passport') {
                                const isArabicContext = selectedCountry?.language === 'AR' || applyRTLStyling(msg.text) || msg.text.includes('رفع جواز السفر');
                                window.lastActionWasPassport = true;
                                window.isArabicPassportFlow = isArabicContext;
                                fileInputRef.current.setAttribute('data-passport', 'true');
                                fileInputRef.current.click();
                              } else if (card.action === 'scan_passport') {
                                const isArabicContext = selectedCountry?.language === 'AR' || applyRTLStyling(msg.text) || msg.text.includes('مسح جواز السفر');
                                window.isArabicPassportFlow = isArabicContext;
                                handleCameraCapture();
                              } else if (card.action === 'seat_map') {
                                // Show seat map with typing indicator
                                const isArabicContext = selectedCountry?.language === 'AR' || applyRTLStyling(msg.text);
                                const userMessage = {
                                  id: `user-${Date.now()}`,
                                  sender: 'user',
                                  text: isArabicContext ? 'عرض خريطة المقاعد' : 'Show seat map',
                                  timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                };
                                setChatMessages(prev => [...prev, userMessage, { id: `typing-${Date.now()}`, sender: 'Assistant', text: '', isTyping: true }]);
                                
                                chatAPI.sendMessage({
                                  user: { id: sessionId, name: 'Demo User' },
                                  query: isArabicContext ? 'عرض خريطة المقاعد' : 'show seat map',
                                  conversation_history: conversationHistory,
                                  country: selectedCountry?.code
                                }).then(response => {
                                  setChatMessages(prev => {
                                    const updated = [...prev];
                                    updated.pop(); // Remove typing indicator
                                    updated.push({
                                      id: `msg-${Date.now()}`,
                                      text: response.data.response,
                                      sender: 'Assistant',
                                      isSeatMap: true,
                                      timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                    });
                                    return updated;
                                  });
                                });
                              } else if (card.action === 'continue_booking') {
                                // Continue with booking flow
                                const userMessage = {
                                  id: `user-${Date.now()}`,
                                  sender: 'user',
                                  text: 'Continue with booking',
                                  timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                };
                                setChatMessages(prev => [...prev, userMessage]);
                                
                                chatAPI.sendMessage({
                                  user: { id: sessionId, name: 'Demo User' },
                                  query: 'continue booking',
                                  conversation_history: conversationHistory,
                                  country: selectedCountry?.code
                                }).then(response => {
                                  const continueMessage = {
                                    id: `msg-${Date.now()}`,
                                    text: response.data.response,
                                    sender: 'Assistant',
                                    timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                  };
                                  setChatMessages(prev => [...prev, continueMessage]);
                                });
                              }
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '10px 12px',
                              backgroundColor: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '500',
                              color: '#374151',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#004A97';
                              e.target.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#f8fafc';
                              e.target.style.color = '#374151';
                            }}
                          >
                            <span style={{ fontSize: '16px' }}>{card.icon}</span>
                            <span>{card.text}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* Feedback and TTS buttons for Assistant messages */}
                    {msg.sender === 'Assistant' && !msg.isTyping && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                        <div style={{
                          fontSize: '11px',
                          color: '#004A97',
                          direction: 'ltr',
                          textAlign: 'left'
                        }}>
                          {msg.timestamp}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', height: '24px' }}>
                        {/* Feedback buttons */}
                        <MemoizedButton
                          onClick={() => handleFeedback(idx, 'like')}
                          style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Like this response"
                        >
                          <ThumbsUp size={16} color={feedbackStates[idx]?.type === 'like' ? '#10b981' : '#004A97'} />
                        </MemoizedButton>
                        
                        <MemoizedButton
                          onClick={() => handleFeedback(idx, 'dislike')}
                          style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Dislike this response"
                        >
                          <ThumbsDown size={16} color={feedbackStates[idx]?.type === 'dislike' ? '#ef4444' : '#004A97'} />
                        </MemoizedButton>
                        
                        <MemoizedButton
                          onClick={() => handleFeedback(idx, 'feedback')}
                          style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Provide detailed feedback"
                        >
                          <MessageSquare size={16} color={feedbackStates[idx]?.showForm ? '#004A97' : '#004A97'} />
                        </MemoizedButton>
                        
                        {/* Copy Button */}
                        <MemoizedButton
                          onClick={() => handleCopyText(msg.text)}
                          style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Copy answer"
                        >
                          <Copy size={16} color="#004A97" />
                        </MemoizedButton>
                        
                        {/* TTS Button */}
                        {(detectLanguage(msg.text) === 'ar' || detectLanguage(msg.text) === 'en') && (
                          <button
                            onClick={() => handleTTSClick(msg.text, idx)}
                            style={{
                              padding: '4px',
                              backgroundColor: isSpeaking ? '#004A97' : 'transparent',
                              border: isSpeaking ? '1px solid #004A97' : 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              height: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSpeaking) e.target.style.backgroundColor = '#f0f0f0';
                            }}
                            onMouseLeave={(e) => {
                              if (!isSpeaking) e.target.style.backgroundColor = 'transparent';
                            }}
                            title={currentSpeakingMessage === idx ? 'Stop audio' : (detectLanguage(msg.text) === 'ar' ? "اضغط لسماع الرسالة بالصوت" : "Click to read this message aloud")}
                          >
                            {currentSpeakingMessage === idx ? (
                              <div style={{
                                width: '8px',
                                height: '8px',
                                backgroundColor: 'white',
                                borderRadius: '50%',
                                animation: 'pulse 1s infinite'
                              }}></div>
                            ) : (
                              <div style={{
                                width: 0,
                                height: 0,
                                borderTop: '6px solid transparent',
                                borderBottom: '6px solid transparent',
                                borderLeft: '10px solid #004A97',
                                marginLeft: '2px'
                              }}></div>
                            )}
                          </button>
                        )}
                        </div>
                      </div>
                    )}
                  </div>
                  

                  

                  

                  

                  

                </div>
                
                {/* Images section for assistant responses - full width */}
                {msg.sender !== 'user' && msg.images && msg.images.length > 0 && (
                  <div style={{
                    width: '100%',
                    marginTop: '10px',
                    marginBottom: '10px'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '12px',
                      color: '#2c3e50',
                      paddingLeft: '0px'
                    }}>
                       Visual Results ({msg.images.length} image{msg.images.length > 1 ? 's' : ''})
                    </div>
                    <div style={{
                      display: 'flex',
                      overflowX: 'auto',
                      gap: '12px',
                      padding: '8px 0',
                      scrollbarWidth: 'thin',
                      scrollbarColor: '#cbd5e0 #f7fafc',
                      width: '100%'
                    }}>
                      {msg.images.map((img, imgIdx) => (
                        <div key={imgIdx} style={{
                          minWidth: isFullscreen ? '320px' : '300px',
                          maxWidth: isFullscreen ? '320px' : '300px',
                          backgroundColor: '#ffffff',
                          borderRadius: '12px',
                          padding: '12px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          border: '1px solid #e5e7eb',
                          flexShrink: 0
                        }}>
                          <img 
                            src={(() => {
                              const imageUrl = chatAPI.getAgentImage(img.filename.split('/')[0], img.filename.split('/')[1]);
                              console.log('Loading image:', img.filename, 'URL:', imageUrl);
                              return imageUrl;
                            })()}
                            alt={img.description}
                            style={{
                              width: '100%',
                              height: isFullscreen ? '220px' : '200px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              marginBottom: '10px',
                              cursor: 'pointer',
                              transition: 'transform 0.2s ease',
                              backgroundColor: '#f3f4f6'
                            }}
                            onError={(e) => {
                              console.error('Image failed to load:', e.target.src);
                              console.error('Image filename:', img.filename);
                              console.error('Image description:', img.description);
                              e.target.style.display = 'none';
                              const placeholder = e.target.parentNode.querySelector('.image-placeholder');
                              if (placeholder) placeholder.style.display = 'flex';
                            }}
                            onLoad={(e) => {
                              console.log('Image loaded successfully:', e.target.src);
                            }}
                            onClick={(e) => {
                              e.target.style.transform = e.target.style.transform === 'scale(1.5)' ? 'scale(1)' : 'scale(1.5)';
                            }}
                          />
                          <div className="image-placeholder" style={{
                            display: 'none',
                            height: isFullscreen ? '220px' : '200px',
                            backgroundColor: '#f3f4f6',
                            borderRadius: '8px',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            color: '#6b7280',
                            fontSize: '14px',
                            marginBottom: '10px'
                          }}>
                            <div style={{ fontSize: '24px', marginBottom: '8px' }}>🖼️</div>
                            <div>Image not available</div>
                            <div style={{ fontSize: '12px', marginTop: '4px' }}>{img.filename}</div>
                          </div>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: '500',
                            color: '#2c3e50',
                            lineHeight: '1.3'
                          }}>
                            {img.description}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                

                {/* Image display for user messages with images */}
                {msg.sender === 'user' && msg.hasImage && msg.imagePreview && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    position: 'absolute',
                    top: '180px',
                    left: '24px',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      maxWidth: '250px',
                      backgroundColor: '#ffffff',
                      borderRadius: '12px',
                      padding: '8px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                    }}>
                      <img 
                        src={msg.imagePreview} 
                        alt="Uploaded" 
                        style={{ 
                          width: '100%',
                          maxHeight: '200px', 
                          borderRadius: '8px',
                          objectFit: 'cover'
                        }} 
                      />
                      <div style={{ 
                        marginTop: '6px', 
                        fontStyle: 'italic', 
                        fontSize: '12px',
                        color: '#6b7280',
                        textAlign: 'center'
                      }}>
                        📷 Image uploaded
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Edited indicator for user messages */}
                {msg.sender === 'user' && msg.isEdited && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: '4px'
                  }}>
                    <span style={{
                      fontSize: '11px',
                      color: '#6b7280',
                      fontStyle: 'italic',
                      backgroundColor: '#f3f4f6',
                      padding: '2px 6px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}>
                      ✏️ edited
                    </span>
                  </div>
                )}
                
                {/* Magic and Edit icons below user messages - only show magic after response */}
                {msg.sender === 'user' && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '2px',
                    marginTop: '4px',
                    marginBottom: '6px'
                  }}>
                    {/* Show magic button only if magic queries are ready */}
                    {(() => {
                      const userMessageId = msg.id;
                      const magicKey = `magic_${userMessageId}`;
                      const hasResponse = idx < chatMessages.length - 1 && chatMessages[idx + 1]?.sender === 'Assistant' && !chatMessages[idx + 1]?.text.includes('typing');
                      const magicReady = magicCache[magicKey] && magicCache[magicKey].queries && magicCache[magicKey].queries.length > 0;
                      
                      return hasResponse && magicReady && (
                        <button
                          onClick={() => handleMagicAssistant(msg.text, idx)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '2px'
                          }}
                          title="AI Magic Assistant"
                        >
                          <img src="/images_all/magic_button.png" alt="Magic" style={{ width: '24px', height: '24px' }} />
                        </button>
                      );
                    })()}
                    
                    <button
                      onClick={() => handleEditMessage(idx, msg.text)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '2px'
                      }}
                      title="Edit message"
                    >
                      <img src="/images_all/edit_button.png" alt="Edit" style={{ width: '24px', height: '24px' }} />
                    </button>
                    
                    <button
                      onClick={() => handleCopyText(msg.text)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '2px'
                      }}
                      title="Copy query"
                    >
                      <Copy size={20} color="#004A97" />
                    </button>
                  </div>
                )}
                
                {/* Related queries outside prompt bubble */}
                {msg.sender === 'Assistant' && !msg.isInitialPrompt && msg.prompts && msg.prompts.length > 0 && (
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    marginTop: '10px',
                    marginBottom: '10px'
                  }}>
                    {msg.prompts.map((prompt, index) => (
                      <button
                        key={index}
                        onClick={async () => {
                          // Handle special actions
                          if (prompt === 'Upload Passport') {
                            window.lastActionWasPassport = true;
                            fileInputRef.current.setAttribute('data-passport', 'true');
                            fileInputRef.current.click();
                            return;
                          }
                          
                          if (prompt === 'Scan Passport') {
                            handleCameraCapture();
                            return;
                          }
                          
                          // Regular chat flow
                          const userMessage = { 
                            id: `user-${Date.now()}`,
                            sender: 'user', 
                            text: prompt,
                            hasImage: false,
                            imagePreview: null,
                            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                          };
                          
                          setChatMessages((prev) => [
                            ...prev,
                            userMessage,
                            { id: `typing-${Date.now()}`, sender: 'Assistant', text: '', isTyping: true },
                          ]);
                          
                          setIsLoading(true);
                          
                          try {
                            const response = await chatAPI.sendMessage({
                              user: { id: sessionId, name: 'Demo User' }, 
                              query: prompt,
                              conversation_history: conversationHistory,
                              country: selectedCountry?.code
                            });
                            
                            const data = response.data;
                            
                            // Update session and history
                            if (data.session_id) setSessionId(data.session_id);
                            if (data.conversation_history) setConversationHistory(data.conversation_history);
                            
                            setChatMessages((prev) => {
                              const updated = [...prev];
                              updated.pop(); // remove "typing..." message
                              
                              const isHtmlCard = data.html_card && data.html_card.trim().length > 0;
                              
                              updated.push({
                                id: `msg-${Date.now()}`,
                                sender: 'Assistant',
                                text: data.response || 'Sorry, I couldn\'t process your request.',
                                agent: data.agent || 'unknown',
                                images: data.images,
                                html_card: data.html_card,
                                isHtmlCard: isHtmlCard,
                                isPromptBubble: true,
                                prompts: data.related_queries || [],
                                related_queries: data.related_queries,
                                timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                              });
                              
                              return updated;
                            });
                          } catch (err) {
                            console.error('Chat error:', err);
                            setChatMessages((prev) => {
                              const updated = [...prev];
                              updated.pop(); // remove "typing..." message
                              return [
                                ...updated,
                                {
                                  id: `msg-${Date.now()}`,
                                  sender: 'Assistant',
                                  text: `Sorry, there was an error processing your request: ${err.message}. Please try again.`,
                                  isPromptBubble: true,
                                  prompts: ['Try again', 'Contact support', 'Ask another question']
                                },
                              ];
                            });
                          } finally {
                            setIsLoading(false);
                            // Input already empty
                          }
                        }}
                        style={{
                          background: '#004A97',
                          color: 'white',
                          fontSize: '16px',
                          fontWeight: '500',
                          padding: '8px 12px',
                          borderRadius: '20px',
                          border: 'none',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
                        onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                )}
                </>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Selected Image Preview */}
          {selectedImage && !isMinimized && (
            <div style={{
              padding: '10px',
              borderTop: '1px solid #ccc',
              position: 'relative',
            }}>
              <div style={{
                position: 'relative',
                width: '100px',
                height: '100px',
                margin: '0 auto',
              }}>
                <img
                  src={selectedImage.preview}
                  alt="Selected"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '8px',
                  }}
                />
                <button
                  onClick={handleRemoveImage}
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    background: 'white',
                    borderRadius: '50%',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }}
                >
                  <X size={20} color="#dc3545" />
                </button>
              </div>
            </div>
          )}

          {/* Input Area */}
          <div style={{
            padding: '16px',

            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: 'calc(100% - 48px)',

          }}>
            {/* Input Box */}
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              borderRadius: '9999px',
              border: '2px solid rgba(59, 130, 246, 0.8)',
              padding: '12px 16px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              backdropFilter: 'blur(10px)'
            }}>
              
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={selectedCountry?.language === 'AR' ? 'أدخل استفسارك التالي' : 'Enter next query'}
                style={{
                  flex: 1,
                  outline: 'none',
                  fontSize: '16px',
                  color: '#000000',
                  fontFamily: 'Roboto',
                  fontWeight: '600',
                  border: 'none',
                  backgroundColor: 'transparent',
                  textAlign: applyRTLStyling(input) ? 'right' : 'left',
                  direction: applyRTLStyling(input) ? 'rtl' : 'ltr',
                  minHeight: '28px',
                  padding: '6px 0',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  verticalAlign: 'middle'
                }}
                disabled={isLoading}
                autoComplete="off"
              />
              
              {/* Image Upload Button */}
              <input
                type="file"
                onChange={handleImageSelect}
                style={{ display: 'none' }}
                ref={fileInputRef}
                disabled={isLoading}
              />
              <button
                onClick={async () => {
                  try {
                    let disabledAgents = [];
                    
                    try {
                      const statusResponse = await fetch('https://jzdt-dev-func-ai-test.azurewebsites.net/api/dashboard/status');
                      if (statusResponse.ok) {
                        const statusData = await statusResponse.json();
                        disabledAgents = statusData.disabled_agents || [];
                      }
                    } catch (err) {
                      // Fallback to localStorage
                      disabledAgents = JSON.parse(localStorage.getItem('chatbot_disabled_agents') || '[]');
                    }
                    
                    const isArabicContext = selectedCountry?.language === 'AR';
                    
                    if (disabledAgents.includes('image_analysis_agent') || 
                        disabledAgents.includes('image_display_agent')) {
                      setChatMessages(prev => [
                        ...prev,
                        {
                          id: `msg-${Date.now()}`,
                          sender: 'Assistant',
                          text: isArabicContext ? 
                            '📷 رفع الصور معطل حالياً. خدمات تحليل الصور قيد الصيانة.' :
                            '📷 Image upload is currently disabled. Image analysis services are under maintenance.',
                          timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                        }
                      ]);
                      return;
                    }
                  } catch (err) {
                    console.log('Could not check image service status:', err);
                  }
                  
                  // Set Arabic context for regular image upload
                  window.isArabicImageFlow = selectedCountry?.language === 'AR';
                  fileInputRef.current.click();
                }}
                style={{
                  width: '24px',
                  height: '24px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '8px'
                }}
                title="Upload Image (JPG, PNG, GIF, WEBP, BMP - Max 10MB)"
                disabled={isLoading}
              >
                <img src="/images_all/imageupload.png" alt="Upload" style={{ width: '20px', height: '20px' }} />
              </button>
              
              {/* Microphone/Send Button */}
              {input.trim() || selectedImage ? (
                <button
                  onClick={handleSend}
                  style={{
                    padding: '8px',
                    background: 'transparent',
                    borderRadius: '50%',
                    border: 'none',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    opacity: isLoading ? 0.7 : 1
                  }}
                  title={isLoading ? 'Processing...' : 'Send message'}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div style={{
                      width: '12px',
                      height: '12px',
                      border: '2px solid transparent',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite'
                    }} />
                  ) : (
                    <img 
                      src="/images_all/rail-arrow-button.png" 
                      alt="Send" 
                      style={{
                        width: '18px',
                        height: '18px',
                        objectFit: 'contain'
                      }}
                    />
                  )}
                </button>
              ) : (
                <button
                  onClick={handleMicrophoneClick}
                  style={{
                    padding: '8px',
                    background: 'linear-gradient(305.32deg, #004A97 -7.93%, #3FB4E5 108.38%)',
                    border: 'none',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px'
                  }}
                  title={isListening ? 'Stop listening' : 'Voice input'}
                  disabled={isLoading}
                >
                  {isListening ? (
                    <X size={24} color="#FFFFFF" />
                  ) : (
                    <Mic size={24} color="#FFFFFF" />
                  )}
                </button>
              )}
            </div>
          </div>
          
          {/* Edit Message Overlay */}
          {editingMessage !== null && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1002
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '24px',
                width: '90%',
                maxWidth: '450px',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                border: '1px solid #e5e7eb',
                position: 'relative'
              }}>
                <h3 style={{
                  margin: '0 0 12px 0',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  Edit Message
                </h3>
                
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  style={{
                    width: 'calc(100% - 24px)',
                    minHeight: '100px',
                    padding: '16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '15px',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    marginBottom: '20px',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    lineHeight: '1.5'
                  }}
                  placeholder="Edit your message..."
                  onFocus={(e) => e.target.style.borderColor = '#004A97'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
                
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setEditingMessage(null);
                      setEditText('');
                    }}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: 'white',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveEditedMessage}
                    disabled={!editText.trim() || isEditLoading}
                    style={{
                      padding: '6px 12px',
                      border: 'none',
                      borderRadius: '6px',
                      background: isEditLoading ? '#6b7280' : (editText.trim() ? '#3b82f6' : '#9ca3af'),
                      color: 'white',
                      cursor: (editText.trim() && !isEditLoading) ? 'pointer' : 'not-allowed',
                      fontSize: '13px',
                      fontWeight: '500',
                      transition: 'all 0.1s ease',
                      transform: 'scale(1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    onMouseDown={(e) => !isEditLoading && (e.target.style.transform = 'scale(0.95)')}
                    onMouseUp={(e) => !isEditLoading && (e.target.style.transform = 'scale(1)')}
                    onMouseLeave={(e) => !isEditLoading && (e.target.style.transform = 'scale(1)')}
                  >
                    {isEditLoading && (
                      <div style={{
                        width: '12px',
                        height: '12px',
                        border: '2px solid transparent',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite'
                      }} />
                    )}
                    {isEditLoading ? 'Sending...' : 'Send Edited'}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Magic Assistant Loading Popup */}
          {magicLoading !== null && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1003
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '30px',
                width: '85%',
                maxWidth: '300px',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
                textAlign: 'center'
              }}>
                <div 
                  className="magic-spinner-large"
                  style={{
                    width: '50px',
                    height: '50px',
                    border: '5px solid #e2e8f0',
                    borderTop: '5px solid #004A97',
                    borderRight: '5px solid #3FB4E5',
                    borderRadius: '50%',
                    margin: '0 auto 20px auto',
                    boxShadow: '0 0 20px rgba(0, 74, 151, 0.3)'
                  }}
                />
                <h3 style={{
                  margin: '0 0 8px 0',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  ✨ Generating Related Questions
                </h3>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  color: '#6b7280'
                }}>
                  Please wait while I create personalized follow-up questions for you...
                </p>
              </div>
            </div>
          )}
          
          {/* Magic Assistant Results Popup */}
          {magicPopup.show && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1003
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '20px',
                width: '85%',
                maxWidth: '350px',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '16px'
                }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      margin: '0 0 8px 0',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: magicPopup.isError ? '#dc2626' : magicPopup.isTimeout ? '#f59e0b' : '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{ fontSize: '18px' }}>
                        {magicPopup.isError ? '⚠️' : magicPopup.isTimeout ? '⏱️' : '✨'}
                      </span>
                      {magicPopup.isError ? 'Generation Failed' : magicPopup.isTimeout ? 'Request Timeout' : 'Related Questions'}
                    </h3>
                    
                    {(magicPopup.isError || magicPopup.isTimeout) && (
                      <p style={{
                        margin: 0,
                        fontSize: '14px',
                        color: '#6b7280'
                      }}>
                        {magicPopup.isTimeout 
                          ? 'The request took too long to complete.'
                          : 'Something went wrong.'
                        }
                      </p>
                    )}
                  </div>
                  
                  <button
                    onClick={() => setMagicPopup({ show: false, queries: [], messageIndex: null, isError: false, isTimeout: false, errorMessage: null })}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      color: '#6b7280',
                      alignSelf: 'flex-start'
                    }}
                  >
                    <X size={18} />
                  </button>
                </div>
                
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  {magicPopup.queries.map((query, index) => (
                    <button
                      key={index}
                      onClick={async () => {
                        setMagicPopup({ show: false, queries: [], messageIndex: null, isError: false, isTimeout: false, errorMessage: null });
                        
                        // Check if we have cached response
                        let cachedData = null;
                        if (magicPopup.responses && magicPopup.responses.length > 0) {
                          cachedData = magicPopup.responses.find(item => item.query === query);
                        }
                        
                        // Add user message
                        const userMessage = { 
                          id: `user-${Date.now()}`,
                          sender: 'user', 
                          text: query,
                          hasImage: false,
                          imagePreview: null,
                          timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                        };
                        
                        setChatMessages((prev) => [
                          ...prev,
                          userMessage
                        ]);
                        
                        if (cachedData && cachedData.response) {
                          // Use cached response instantly
                          setTimeout(() => {
                            setChatMessages((prev) => {
                              const isHtmlCard = cachedData.html_card && cachedData.html_card.trim().length > 0;
                              
                              return [
                                ...prev,
                                {
                                  id: `msg-${Date.now()}`,
                                  sender: 'Assistant',
                                  text: cachedData.response,
                                  agent: cachedData.agent || 'unknown',
                                  images: cachedData.images,
                                  html_card: cachedData.html_card,
                                  isHtmlCard: isHtmlCard,
                                  isPromptBubble: true,
                                  prompts: cachedData.related_queries || [],
                                  related_queries: cachedData.related_queries,
                                  timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                }
                              ];
                            });
                          }, 100); // Small delay to show it's responding
                        } else {
                          // Fallback to regular API call
                          setChatMessages((prev) => [
                            ...prev,
                            { id: `typing-${Date.now()}`, sender: 'Assistant', text: '', isTyping: true }
                          ]);
                          
                          setIsLoading(true);
                          
                          try {
                            const response = await chatAPI.sendMessage({
                              user: { id: sessionId, name: 'Demo User' }, 
                              query: query,
                              conversation_history: conversationHistory,
                              country: selectedCountry?.code
                            });
                            
                            const data = response.data;
                            
                            setChatMessages((prev) => {
                              const updated = [...prev];
                              updated.pop(); // remove "typing..." message
                              
                              const isHtmlCard = data.html_card && data.html_card.trim().length > 0;
                              
                              updated.push({
                                id: `msg-${Date.now()}`,
                                sender: 'Assistant',
                                text: data.response || 'Sorry, I couldn\'t process your request.',
                                agent: data.agent || 'unknown',
                                images: data.images,
                                html_card: data.html_card,
                                isHtmlCard: isHtmlCard,
                                isPromptBubble: true,
                                prompts: data.related_queries || [],
                                related_queries: data.related_queries,
                                timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                              });
                              
                              return updated;
                            });
                          } catch (err) {
                            console.error('Chat error:', err);
                            setChatMessages((prev) => {
                              const updated = [...prev];
                              updated.pop(); // remove "typing..." message
                              return [
                                ...updated,
                                {
                                  id: `msg-${Date.now()}`,
                                  sender: 'Assistant',
                                  text: `Sorry, there was an error processing your request: ${err.message}. Please try again.`,
                                  isPromptBubble: true,
                                  prompts: ['Try again', 'Contact support', 'Ask another question']
                                },
                              ];
                            });
                          } finally {
                            setIsLoading(false);
                          }
                        }
                      }}
                      style={{
                        padding: '12px 16px',
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontSize: '14px',
                        color: '#374151',
                        transition: 'all 0.2s ease',
                        fontWeight: '500'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#184b92';
                        e.target.style.color = 'white';
                        e.target.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#f8fafc';
                        e.target.style.color = '#374151';
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      {query}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Feedback Popup Overlay */}
          {(Object.keys(feedbackStates).some(key => feedbackStates[key]?.showForm) || Object.keys(feedbackStates).some(key => feedbackStates[key]?.showThankYou)) && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1001
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '10px',
                padding: '16px',
                width: '80%',
                maxWidth: '300px',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
                textAlign: 'center'
              }}>
                {Object.keys(feedbackStates).some(key => feedbackStates[key]?.showThankYou) ? (
                  // Thank you message
                  <div>
                    <div style={{
                      fontSize: '48px',
                      marginBottom: '16px'
                    }}>
                      ✓
                    </div>
                    <h3 style={{
                      margin: '0 0 8px 0',
                      fontSize: '20px',
                      fontWeight: '600',
                      color: '#10b981'
                    }}>
                      Thank you!
                    </h3>
                    <p style={{
                      margin: '0',
                      fontSize: '14px',
                      color: '#6b7280'
                    }}>
                      Your feedback has been submitted successfully.
                    </p>
                  </div>
                ) : (
                  // Feedback form
                  <div style={{ textAlign: 'left' }}>
                    <h3 style={{
                      margin: '0 0 12px 0',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#374151',
                      textAlign: 'center'
                    }}>
                      Feedback
                    </h3>
                    
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '6px' }}>
                        Rate this response:
                      </label>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        {[1, 2, 3, 4, 5].map(star => {
                          const activeIdx = Object.keys(feedbackStates).find(key => feedbackStates[key]?.showForm);
                          return (
                            <button
                              key={star}
                              onClick={() => handleFeedback(activeIdx, 'feedback', star, feedbackStates[activeIdx]?.comment || '')}
                              style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '20px',
                                cursor: 'pointer',
                                color: (feedbackStates[activeIdx]?.rating || 0) >= star ? '#fbbf24' : '#d1d5db',
                                transition: 'color 0.2s'
                              }}
                            >
                              ★
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '6px' }}>
                        Additional comments:
                      </label>
                      <textarea
                        value={(() => {
                          const activeIdx = Object.keys(feedbackStates).find(key => feedbackStates[key]?.showForm);
                          return feedbackStates[activeIdx]?.comment || '';
                        })()}
                        onChange={(e) => {
                          const activeIdx = Object.keys(feedbackStates).find(key => feedbackStates[key]?.showForm);
                          handleFeedback(activeIdx, 'feedback', feedbackStates[activeIdx]?.rating, e.target.value);
                        }}
                        placeholder="Tell us how we can improve..."
                        style={{
                          width: '100%',
                          minHeight: '60px',
                          padding: '8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '13px',
                          resize: 'vertical',
                          fontFamily: 'inherit'
                        }}
                      />
                    </div>
                    
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => {
                          const activeIdx = Object.keys(feedbackStates).find(key => feedbackStates[key]?.showForm);
                          handleFeedback(activeIdx, null);
                        }}
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          background: 'white',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '500'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          const activeIdx = Object.keys(feedbackStates).find(key => feedbackStates[key]?.showForm);
                          submitFeedback(activeIdx);
                        }}
                        style={{
                          padding: '6px 12px',
                          border: 'none',
                          borderRadius: '6px',
                          background: '#3b82f6',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '500'
                        }}
                      >
                        Submit Feedback
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ChatbotFixed;
      <style>
        {`
          input::placeholder {
            color: #374151 !important;
            opacity: 1 !important;
            font-weight: 600 !important;
            font-size: 16px !important;
          }
        `}
      </style>